import { useEffect, useState, useMemo } from 'react';
import { useExportGate } from '../hooks/useExportGate';
import { isSelectableProject } from '../lib/projectStatus';
import { TransferBoqDialog } from '../components/boq/TransferBoqDialog';
import { usePlanPermissions } from '../hooks/usePlanPermissions';
import { promptUpgrade } from '../components/billing/PlanUpgradeModal';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { FileText, ArrowRight, Trash2, AlertTriangle, CheckCircle, Download, FileSpreadsheet, Pencil, Save, X, Plus, Link2Off, ClipboardList, Receipt, Calendar, Lock, Unlock, Share2, Layers, Users, Building2, UserCircle, Link, ExternalLink, Clock, CalendarClock, Copy, Eye, RefreshCw, ChevronDown, LayoutDashboard, Tags, Search, ShoppingCart, Wand2, CalendarCheck, Clipboard, CheckCircle2, GripVertical, ChevronUp, ClipboardCheck, TrendingUp, Settings2, GitMerge, Archive, ArchiveRestore } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '@/App';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate } from '@/utils/dateFormat';
import SearchableCombobox from '@/components/SearchableCombobox';
import CreateProjectDialog from '@/components/CreateProjectDialog';
import EmailComposeDialog from '@/components/EmailComposeDialog';
import PaymentSchedule from '@/components/PaymentSchedule';
import PaymentTracker from '@/components/PaymentTracker';
import { DeleteBOQDialog, DeleteItemDialog, UnlockBOQDialog, PublishClientDialog, PublishVendorDialog, PublicLinkDialog, ConfirmBOQDialog } from '@/components/boq/BOQDialogs';
import { Mail } from 'lucide-react';

const GST_BRACKETS = [
  { value: '0', label: '0%' },
  { value: '5', label: '5%' },
  { value: '12', label: '12%' },
  { value: '18', label: '18%' },
  { value: '28', label: '28%' },
];

const UNIT_OPTIONS = ['No.s', 'Sq.ft', 'R.ft', 'L.ft', 'Kg', 'Ltr', 'Set', 'Pair', 'Box', 'Bag', 'Lot'];

// Rate layer options for toggle
const RATE_LAYERS = [
  { value: 'vendor', label: 'Vendor Rate', color: 'bg-purple-500', description: 'Base input rate' },
  { value: 'admin', label: 'Admin Rate', color: 'bg-stone-700', description: 'Vendor ÷ (1 - Markdown%)' },
  { value: 'client', label: 'Client Rate', color: 'bg-blue-500', description: 'Admin × (1 + Markup%)' },
];

export default function BOQWorksheets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // SECURITY: Only owner/staff can access BOQ Worksheets page
  // Clients should use /client/boq, Vendors should use /vendor/boq
  const isAuthorized = ['owner', 'staff', 'admin', 'project_manager'].includes(user.role);
  
  const [worksheets, setWorksheets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('all');
  // iter-318: Active/Inactive filter for BOQ worksheets (mirrors Projects UX)
  const [activeFilter, setActiveFilter] = useState('active'); // 'active' | 'inactive' | 'all'
  // iter-338z8: Empty BOQ direct-create — bypasses the "create tasks first" flow.
  const [newBoqDialogOpen, setNewBoqDialogOpen] = useState(false);
  const [newBoqProjectId, setNewBoqProjectId] = useState('');
  // iter-381: allow admins to spin up a new project from the Create BOQ
  // dialog. The picker's "+ Create new project" action opens this modal;
  // on success the fresh project is auto-selected.
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [newBoqDisplayName, setNewBoqDisplayName] = useState('');
  const [creatingNewBoq, setCreatingNewBoq] = useState(false);

  // iter-338z10: Auto-open the New BOQ dialog when Dashboard's Quick Action
  // navigates here with ?action=new. We consume the flag by stripping it
  // from the URL so a page refresh doesn't re-open the dialog.
  // iter-338z14: also honour ?project_id=X to pre-select in the dialog
  // for "project-aware" deep-links (e.g. from a ProjectDetail page).
  useEffect(() => {
    if (searchParams.get('action') === 'new' && isAuthorized) {
      setNewBoqDialogOpen(true);
      const pid = searchParams.get('project_id');
      if (pid) setNewBoqProjectId(pid);
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      next.delete('project_id');
      setSearchParams(next, { replace: true });
    }
    // iter-365 — deep links from Edit BOQ page's Admin Tools dropdown.
    const adminKey = searchParams.get('admin');
    if (adminKey && isAuthorized) {
      const src = searchParams.get('source');
      const proj = searchParams.get('project');
      if (adminKey === 'clone-boq') {
        setCloneBOQData((prev) => ({ ...prev, source_project_id: src || prev.source_project_id || '' }));
        setCloneBOQDialogOpen(true);
      } else if (adminKey === 'quote-requests') {
        // Best-effort: pre-filter list to this project if any state supports it.
        if (proj) setVendorQuoteData((prev) => ({ ...prev, project_id: proj }));
        fetchVendorQuoteRequests();
        setViewQuoteRequestsDialogOpen(true);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('admin'); next.delete('source'); next.delete('project');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, isAuthorized, setSearchParams]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [selectedWorksheet, setSelectedWorksheet] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteAction, setDeleteAction] = useState('unlink');
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  // NEW: Rate layer toggle state
  // iter-313: Default rate layer = Client (blue C circle). Default view = Actual (Site)
  // so the headline Total Value reflects the client's site-corrected figures, not estimates.
  const [rateLayer, setRateLayer] = useState('client');
  const [useActualValues, setUseActualValues] = useState(true);

  // P1 — Bulk selection + bulk actions (delete / confirm / reject / pending)
  const [selectedBoqIds, setSelectedBoqIds] = useState(() => new Set());
  const [bulkActionRunning, setBulkActionRunning] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');

  const toggleBoqSelected = (id) => {
    setSelectedBoqIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const setProjectSelection = (ids, checked) => {
    setSelectedBoqIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => { if (checked) next.add(id); else next.delete(id); });
      return next;
    });
  };
  const clearSelection = () => setSelectedBoqIds(new Set());

  // iter-293: Merge multiple BOQs into a single new draft worksheet.
  // Only enabled when 2+ BOQs sharing the SAME project_id are selected.
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeName, setMergeName] = useState('');
  const [mergeSmart, setMergeSmart] = useState(true); // iter-294: dedupe identical lines
  const [merging, setMerging] = useState(false);

  const selectedBoqsForMerge = useMemo(() => {
    const ids = Array.from(selectedBoqIds);
    return worksheets.filter(w => ids.includes(w.id));
  }, [selectedBoqIds, worksheets]);

  const mergeProjectIds = useMemo(() => {
    return new Set(selectedBoqsForMerge.map(w => w.project_id).filter(Boolean));
  }, [selectedBoqsForMerge]);

  const canMerge = selectedBoqIds.size >= 2 && mergeProjectIds.size === 1;
  const [transferMode, setTransferMode] = useState(null); // Fix 6: 'copy' | 'move'
  const { exportLocked, guardExport } = useExportGate();
  const { can: planCan, loaded: planLoaded, required_plans: planReq } = usePlanPermissions();
  const openTransfer = (mode) => {
    if (planLoaded && !planCan('can_copy_move_boq')) {
      promptUpgrade('can_copy_move_boq', planReq?.can_copy_move_boq, 'Multi-BOQ Copy/Move is an Enterprise feature.');
      return;
    }
    setTransferMode(mode);
  };

  const openMergeDialog = () => {
    if (selectedBoqIds.size < 2) {
      toast.error('Select at least 2 BOQs to merge');
      return;
    }
    if (mergeProjectIds.size !== 1) {
      toast.error('All selected BOQs must belong to the same project');
      return;
    }
    setMergeName('');
    setMergeDialogOpen(true);
  };

  const performMerge = async () => {
    setMerging(true);
    try {
      const r = await axios.post(`${API}/boq-worksheets/merge`, {
        ids: Array.from(selectedBoqIds),
        name: mergeName.trim() || undefined,
        smart_merge: mergeSmart,
      });
      toast.success(r.data?.message || 'BOQs merged successfully');
      clearSelection();
      setMergeDialogOpen(false);
      setMergeName('');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to merge BOQs');
    } finally {
      setMerging(false);
    }
  };

  const runBulkAction = async (action, opts = {}) => {
    const ids = Array.from(selectedBoqIds);
    if (ids.length === 0) { toast.error('Select at least one BOQ'); return; }
    if (action === 'delete' && !opts.reason) {
      setBulkDeleteDialogOpen(true);
      return;
    }
    setBulkActionRunning(true);
    try {
      const res = await axios.post(`${API}/boq-worksheets/bulk-action`, {
        ids,
        action,
        reason: opts.reason || '',
        notes: opts.notes || '',
      });
      const d = res.data || {};
      if (action === 'delete') {
        toast.success(d.message || `Deleted ${d.succeeded_count} BOQ(s)`);
        if ((d.skipped || []).length > 0) {
          toast.info(`${d.skipped.length} skipped (linked to invoices): ${d.skipped.slice(0, 3).map(s => s.worksheet_number).join(', ')}${d.skipped.length > 3 ? '...' : ''}`);
        }
      } else {
        toast.success(d.message || `Updated ${d.succeeded_count} BOQ(s)`);
      }
      clearSelection();
      setBulkDeleteDialogOpen(false);
      setBulkDeleteReason('');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Bulk ${action} failed`);
    } finally {
      setBulkActionRunning(false);
    }
  };
  
  // NEW: Publish Dialog with Validity Date
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishWorksheet, setPublishWorksheet] = useState(null);
  const [validityDate, setValidityDate] = useState('');
  const [generatePublicLink, setGeneratePublicLink] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // NEW: Publish to Vendor Dialog
  const [publishVendorDialogOpen, setPublishVendorDialogOpen] = useState(false);
  const [publishVendorWorksheet, setPublishVendorWorksheet] = useState(null);
  const [generateVendorPublicLink, setGenerateVendorPublicLink] = useState(false);
  const [publishingVendor, setPublishingVendor] = useState(false);
  
  // NEW: Public Link Dialog
  const [publicLinkDialogOpen, setPublicLinkDialogOpen] = useState(false);
  const [currentPublicLink, setCurrentPublicLink] = useState(null);
  
  // NEW: BOQ Summary Dialog
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  // DIFF SHEET — shows what changed between now and the last published snapshot.
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [diffWorksheet, setDiffWorksheet] = useState(null);

  const openDiffSheet = async (worksheet) => {
    setDiffWorksheet(worksheet);
    setDiffOpen(true);
    setDiffLoading(true);
    setDiffData(null);
    try {
      const r = await axios.get(`${API}/boq-worksheets/${worksheet.id}/diff-sheet`);
      setDiffData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load diff sheet');
      setDiffOpen(false);
    } finally {
      setDiffLoading(false);
    }
  };
  const [summaryLoading, setSummaryLoading] = useState(false);
  // iter-313: Default summary view = client (was admin) so the BOQ Summary modal
  // opens straight to the client-perspective view with Actual (Site) figures.
  const [summaryViewRole, setSummaryViewRole] = useState('client');
  
  // Discount state (for footer discount row)
  const [discountType, setDiscountType] = useState('value'); // 'value' or 'percentage'
  const [discountValue, setDiscountValue] = useState(0);
  
  // BOQ Confirmation Dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmWorksheet, setConfirmWorksheet] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState('confirmed');
  const [confirmNotes, setConfirmNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  
  // BOQ Category state for Add Line Item and Edit
  const [boqCategories, setBoqCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [quickAddCategoryOpen, setQuickAddCategoryOpen] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({ name: '', code: '', type: 'main', parentId: '' });
  
  // Inline Task Edit Dialog (opens within BOQ Edit, no navigation)
  const [inlineTaskEditOpen, setInlineTaskEditOpen] = useState(false);
  const [inlineTaskId, setInlineTaskId] = useState(null);
  const [inlineTaskData, setInlineTaskData] = useState(null);
  const [inlineTaskLoading, setInlineTaskLoading] = useState(false);
  const [inlineAiDescLoading, setInlineAiDescLoading] = useState(false);
  
  // BOQ item drag-and-drop state
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);
  
  // Expandable text state for description
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  
  // Category sorting state for Edit BOQ dialog
  const [categorySortBy, setCategorySortBy] = useState('none'); // 'none', 'category_asc', 'category_desc', 'created_asc', 'created_desc'
  
  // Pricing Model Comparison Dialog
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareProjectId, setCompareProjectId] = useState(null);
  const [compareSwitching, setCompareSwitching] = useState(false);
  
  const [newItemData, setNewItemData] = useState({
    title: '',
    description: '',
    quantity: 1,
    unit: 'No.s',
    rate: 0,
    gst_percentage: 18,  // Default GST 18%
    main_category_id: '',
    main_category_code: '',
    main_category_name: '',
    sub_category_id: '',
    sub_category_code: '',
    sub_category_name: '',
  });

  // PHASE 2: Admin Tools state (relocated from Tasks page)
  const [cloneBOQDialogOpen, setCloneBOQDialogOpen] = useState(false);
  const [cloneBOQData, setCloneBOQData] = useState({ source_project_id: '', target_project_id: '', include_descriptions: true });
  const [cloneBOQLoading, setCloneBOQLoading] = useState(false);
  const [vendorQuoteDialogOpen, setVendorQuoteDialogOpen] = useState(false);
  const [vendorQuoteData, setVendorQuoteData] = useState({ project_id: '', vendor_name: '', vendor_email: '', instructions: '', expiry_days: 7 });
  const [vendorQuoteLoading, setVendorQuoteLoading] = useState(false);
  const [vendorQuoteRequests, setVendorQuoteRequests] = useState([]);

  // PHASE 5: Vendor submissions linked to projects
  const [vendorSubmissions, setVendorSubmissions] = useState([]);

  // PHASE 6: Send BOQ via Email
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailProjectId, setEmailProjectId] = useState(null);
  const [viewQuoteRequestsDialogOpen, setViewQuoteRequestsDialogOpen] = useState(false);

  // Final Total state (Grand Total + Additional Items)
  const [additionalItemsSummary, setAdditionalItemsSummary] = useState(null);
  const [savingFinalTotal, setSavingFinalTotal] = useState(false);
  const [finalTotalHistory, setFinalTotalHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);


  useEffect(() => {
    // Redirect unauthorized users
    if (!isAuthorized) {
      toast.error('Access denied. This page is for internal use only.');
      if (user.role === 'client') {
        navigate('/client/boq');
      } else if (user.role === 'vendor') {
        navigate('/vendor/boq');
      } else {
        navigate('/');
      }
      return;
    }
    fetchData();
    fetchBoqCategories(); // Load categories for color coding
    
    // Listen for BOQ edit saves from other tabs
    let bc;
    try {
      bc = new BroadcastChannel('boq_updates');
      bc.onmessage = (event) => {
        if (event.data?.type === 'boq_saved') {
          fetchData();
        }
      };
    } catch (e) { /* BroadcastChannel not supported */ }
    return () => { try { bc?.close(); } catch(e) {} };
  }, [isAuthorized, navigate, user.role, activeFilter]);

  const fetchData = async () => {
    try {
      // Secondary data loads in the background so cards render as soon as BOQs + projects arrive.
      axios.get(`${API}/invoices`).then(r => setInvoices(r.data || [])).catch(() => {});
      axios.get(`${API}/vendor-quote-submissions`).then(r => setVendorSubmissions(r.data || [])).catch(() => {});
      const [worksheetsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/boq-worksheets?active_filter=${activeFilter}&lite=true`),
        axios.get(`${API}/projects?archived=all&limit=1000`),
      ]);
      setWorksheets(worksheetsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load BOQ worksheets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch BOQ Categories for dropdown selection
  const fetchBoqCategories = async () => {
    if (boqCategories.length > 0) return; // Already loaded
    setCategoriesLoading(true);
    try {
      const response = await axios.get(`${API}/boq-categories/for-tasks`);
      setBoqCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load BOQ categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Prepare main category options for SearchableCombobox
  const mainCategoryOptions = useMemo(() => {
    return boqCategories.map(cat => ({
      value: cat.main_category_id || cat.id,
      label: `${cat.group} - ${cat.group_name}`,
      code: cat.group,
      name: cat.group_name,
    }));
  }, [boqCategories]);

  // Prepare sub-category options filtered by selected main category
  const getSubCategoryOptions = (mainCategoryId) => {
    const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === mainCategoryId);
    if (!mainCat) return [];
    // API returns 'categories' not 'sub_categories'
    const subCats = mainCat.categories || mainCat.sub_categories || [];
    return subCats.map(sub => ({
      value: sub.id || sub.code,
      label: `${sub.code} - ${sub.name}`,
      code: sub.code,
      name: sub.name,
    }));
  };

  // Handle main category selection for new item
  const handleMainCategorySelect = (mainCatId) => {
    const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === mainCatId);
    if (mainCat) {
      setNewItemData(prev => ({
        ...prev,
        main_category_id: mainCat.main_category_id || mainCat.id,
        main_category_code: mainCat.group,
        main_category_name: mainCat.group_name,
        sub_category_id: '',
        sub_category_code: '',
        sub_category_name: '',
      }));
    } else {
      setNewItemData(prev => ({
        ...prev,
        main_category_id: '',
        main_category_code: '',
        main_category_name: '',
        sub_category_id: '',
        sub_category_code: '',
        sub_category_name: '',
      }));
    }
  };

  // Handle sub category selection for new item
  const handleSubCategorySelect = (subCatId) => {
    const subOptions = getSubCategoryOptions(newItemData.main_category_id);
    const subCat = subOptions.find(s => s.value === subCatId);
    if (subCat) {
      setNewItemData(prev => ({
        ...prev,
        sub_category_id: subCat.value,
        sub_category_code: subCat.code,
        sub_category_name: subCat.name,
      }));
    } else {
      setNewItemData(prev => ({
        ...prev,
        sub_category_id: '',
        sub_category_code: '',
        sub_category_name: '',
      }));
    }
  };

  // Quick Add Category handler
  const handleQuickAddCategory = async () => {
    if (!newCategoryData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      if (newCategoryData.type === 'main') {
        if (!newCategoryData.code.trim()) {
          toast.error('Category code is required');
          return;
        }
        await axios.post(`${API}/boq-categories/main`, {
          name: newCategoryData.name.trim(),
          code: newCategoryData.code.trim().toUpperCase(),
        });
        toast.success('Main category created');
      } else {
        if (!newCategoryData.parentId) {
          toast.error('Please select a main category first');
          return;
        }
        const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === newCategoryData.parentId);
        await axios.post(`${API}/boq-categories/sub`, {
          main_category_id: newCategoryData.parentId,
          name: newCategoryData.name.trim(),
          code: newCategoryData.code.trim() || `${mainCat?.group || 'X'}${String(Math.floor(Math.random() * 100)).padStart(2, '0')}`,
        });
        toast.success('Sub-category created');
      }
      setQuickAddCategoryOpen(false);
      setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
      // Refresh categories
      setBoqCategories([]);
      await fetchBoqCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  // Get pricing model for a project (from worksheet or project)
  const getWorksheetPricingModel = (worksheet) => {
    return worksheet?.pricing_model || 'vendor_to_client';
  };
  const isCtV = (worksheet) => getWorksheetPricingModel(worksheet) === 'client_to_vendor';

  // Get the base (input) rate for an item based on pricing model
  const getBaseRate = (item, worksheet) => {
    if (isCtV(worksheet)) {
      return parseFloat(item.client_rate || item.rate || 0);
    }
    return parseFloat(item.vendor_rate || item.rate || 0);
  };

  // Calculate comparison data for both pricing models
  const getComparisonData = (projectId) => {
    const projectWorksheets = worksheets.filter(w => w.project_id === projectId && !isRejectedBoq(w));
    if (!projectWorksheets.length) return null;
    const ws = projectWorksheets[0];
    const currentModel = ws.pricing_model || 'vendor_to_client';
    const pct1 = ws.admin_markup_percentage || 20;
    const pct2 = ws.client_markup_percentage || 20;
    
    const allItems = projectWorksheets.flatMap(w => (w.items || []).filter(i => !i.is_discount));
    
    return allItems.slice(0, 10).map(item => {
      const vr = parseFloat(item.vendor_rate || 0);
      const ar = parseFloat(item.admin_rate || 0);
      const cr = parseFloat(item.client_rate || 0);
      const qty = parseFloat(item.quantity || 0);
      
      // Simulate the OTHER model's rates
      let altVr, altAr, altCr;
      if (currentModel === 'vendor_to_client') {
        // Current: V→C. Simulate C→V using current client_rate as input
        altCr = cr;
        altAr = Math.round(cr * (1 - pct1 / 100) * 100) / 100;
        altVr = Math.round(altAr * (1 - pct2 / 100) * 100) / 100;
      } else {
        // Current: C→V. Simulate V→C using current vendor_rate as input
        altVr = vr;
        altAr = Math.round(vr * (1 + pct1 / 100) * 100) / 100;
        altCr = Math.round(altAr * (1 + pct2 / 100) * 100) / 100;
      }
      
      return {
        title: item.task_title || item.title || 'Unnamed',
        qty,
        current: { vr, ar, cr, total: qty * (currentModel === 'vendor_to_client' ? cr : cr) },
        alt: { vr: altVr, ar: altAr, cr: altCr, total: qty * (currentModel === 'vendor_to_client' ? altCr : altCr) },
      };
    });
  };

  // Handle pricing model switch with confirmation
  const handleSwitchPricingModel = async (projectId) => {
    setCompareSwitching(true);
    try {
      await axios.post(`${API}/projects/${projectId}/switch-pricing-model`);
      toast.success('Pricing model switched successfully. Rates recalculated.');
      setCompareDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch pricing model');
    } finally {
      setCompareSwitching(false);
    }
  };

  const hasInvoice = (worksheetId) => {
    return invoices.some((inv) => inv.worksheet_id === worksheetId);
  };

  const getInvoiceNumber = (worksheetId) => {
    const invoice = invoices.find((inv) => inv.worksheet_id === worksheetId);
    return invoice?.invoice_number || '';
  };

  // Project dropdown follows the Active / Archived / All tab.
  const isArchivedProject = (p) => !!p && (p.is_active === false || !!p.archived);
  const tabProjects = projects.filter((p) => {
    if (activeFilter === 'active') return isSelectableProject(p);
    if (activeFilter === 'inactive') return isArchivedProject(p);
    return true;
  });
  useEffect(() => {
    if (selectedProject !== 'all' && projects.length && !tabProjects.some((p) => p.id === selectedProject)) setSelectedProject('all');
  }, [activeFilter, projects]);

  // Filter and group by project
  const filteredWorksheets = selectedProject === 'all' 
    ? worksheets 
    : worksheets.filter(w => w.project_id === selectedProject);

  // Fix 7: rejected BOQs sink to the bottom of each project group
  const isRejectedBoq = (w) => w.confirmation_status === 'rejected';
  const groupedByProject = [...filteredWorksheets].sort((a, b) => Number(isRejectedBoq(a)) - Number(isRejectedBoq(b))).reduce((acc, worksheet) => {
    const projectId = worksheet.project_id;
    if (!acc[projectId]) acc[projectId] = [];
    acc[projectId].push(worksheet);
    return acc;
  }, {});

  // Helper: compute grand total for a worksheet per rate layer (subtotal + GST - discount)
  // iter-313: getGrandTotal now supports actual-site values (default for client view).
  // When `useActual` is true, qty falls back to actual_quantity ?? quantity, and rate
  // falls back to actual_rate ?? layerRate. Vendor view never uses actual_rate (it's
  // a single client-side site adjustment field).
  const getGrandTotal = (w, layer = 'admin', useActual = false) => {
    if (!w.items || w.items.length === 0) return 0;
    
    let subtotal = 0;
    let gstAmount = 0;
    
    for (const item of w.items) {
      if (item.item_type === 'header' || item.is_discount) continue;
      const baseQty = parseFloat(item.quantity || 1);
      const qty = useActual ? parseFloat(item.actual_quantity ?? baseQty) : baseQty;
      let layerRate = 0;
      if (layer === 'vendor') {
        layerRate = parseFloat(item.vendor_rate || 0);
      } else if (layer === 'client') {
        layerRate = parseFloat(item.client_rate || 0);
      } else {
        layerRate = parseFloat(item.admin_rate || item.rate || 0);
      }
      // iter-338d (Msg-680 Bug 2): for ad-hoc imports the Est columns
      // (quantity / vendor_rate / admin_rate / client_rate) are all zero —
      // fall back to the Actual-side tier rate so the V/A/C totals reflect
      // the real cost instead of ₹0.
      if (layerRate === 0) {
        if (layer === 'vendor') layerRate = parseFloat(item.actual_vendor_rate || 0);
        else if (layer === 'client') layerRate = parseFloat(item.actual_client_rate || 0);
        else layerRate = parseFloat(item.actual_admin_rate || item.actual_rate || 0);
      }
      // Apply actual_rate override only on non-vendor layers (actual_rate is captured
      // as a single client-side field without role variants)
      const rate = (useActual && layer !== 'vendor' && item.actual_rate != null)
        ? parseFloat(item.actual_rate)
        : layerRate;
      const qtyResolved = qty > 0 ? qty : parseFloat(item.actual_quantity || 0);
      const itemTotal = qtyResolved * rate;
      subtotal += itemTotal;
      const gstPct = parseFloat(item.gst_percentage ?? 0);
      gstAmount += itemTotal * (gstPct / 100);
    }
    
    subtotal = Math.round(subtotal);
    gstAmount = Math.round(gstAmount);
    
    // Apply discount on the layer's subtotal
    let discount = 0;
    if (w.discount_type === 'percentage' && parseFloat(w.discount_percentage || 0) > 0) {
      discount = Math.round(subtotal * parseFloat(w.discount_percentage) / 100);
    } else if (parseFloat(w.discount_value || 0) > 0) {
      discount = Math.round(parseFloat(w.discount_value));
    }
    
    const afterDiscount = subtotal + gstAmount - discount;
    
    // Include overheads
    const freightPct = parseFloat(w.freight_pct || 0);
    const labourPct = parseFloat(w.labour_handling_pct || 0);
    const consultPct = parseFloat(w.consultancy_pct || 0);
    const overheadFreight = freightPct > 0 ? Math.round(afterDiscount * freightPct / 100) : 0;
    const overheadLabour = labourPct > 0 ? Math.round(afterDiscount * labourPct / 100) : 0;
    const overheadConsult = consultPct > 0 ? Math.round(afterDiscount * consultPct / 100) : 0;
    
    return afterDiscount + overheadFreight + overheadLabour + overheadConsult;
  };

  // Summary counts
  const totalBOQs = worksheets.length;
  const confirmedBOQs = worksheets.filter(w => w.confirmation_status === 'confirmed').length;
  const invoicedBOQs = worksheets.filter(w => hasInvoice(w.id)).length;
  const lockedBOQs = worksheets.filter(w => w.is_locked && !hasInvoice(w.id)).length;
  const pendingBOQs = totalBOQs - invoicedBOQs - lockedBOQs;
  const totalValue = worksheets.filter(w => !isRejectedBoq(w)).reduce((sum, w) => sum + getGrandTotal(w, rateLayer, useActualValues), 0);

  const handleConvertToQuotation = async (worksheetId) => {
    try {
      const response = await axios.post(`${API}/quotations/from-boq/${worksheetId}`);
      toast.success(response.data.message || `Quotation ${response.data.quotation_number} created!`);
      // Refresh worksheets to show locked status
      fetchData();
      navigate('/quotations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    }
  };

  // Create Purchase Order from BOQ (vendor published items)
  const handleCreatePurchaseOrder = async (worksheetId) => {
    try {
      const response = await axios.post(`${API}/purchase-orders/from-boq/${worksheetId}`);
      const pos = response.data.purchase_orders || [];
      if (pos.length === 1) {
        toast.success(`Purchase Order ${pos[0].po_number} created for ${pos[0].vendor_name}!`);
      } else {
        toast.success(response.data.message);
      }
      navigate('/purchase-orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    }
  };
  
  // Inline Task Edit - Opens task edit dialog without navigation
  const openInlineTaskEdit = async (taskId) => {
    if (!taskId) {
      toast.error('No task linked to this item');
      return;
    }
    setInlineTaskId(taskId);
    setInlineTaskLoading(true);
    setInlineTaskEditOpen(true);
    
    try {
      const response = await axios.get(`${API}/tasks/${taskId}`);
      setInlineTaskData(response.data);
    } catch (error) {
      toast.error('Failed to load task details');
      setInlineTaskEditOpen(false);
    } finally {
      setInlineTaskLoading(false);
    }
  };
  
  // Save inline task edit
  const saveInlineTaskEdit = async () => {
    if (!inlineTaskData || !inlineTaskId) return;
    
    try {
      await axios.put(`${API}/tasks/${inlineTaskId}`, {
        title: inlineTaskData.title,
        description: inlineTaskData.description,
        status: inlineTaskData.status,
        start_date: inlineTaskData.start_date,
        end_date: inlineTaskData.end_date,
        quantity: inlineTaskData.quantity,
        unit: inlineTaskData.unit,
        rate: isCtV(selectedWorksheet)
          ? (inlineTaskData._clientRate ?? inlineTaskData.client_rate ?? inlineTaskData.rate)
          : (inlineTaskData._vendorRate ?? inlineTaskData.vendor_rate ?? inlineTaskData.rate),
        vendor_rate: inlineTaskData.vendor_rate,
        client_rate: inlineTaskData.client_rate,
        category_group: inlineTaskData.category_group,
        category_code: inlineTaskData.category_code,
        admin_markup_percentage: inlineTaskData.admin_markup_percentage,
        client_markup_percentage: inlineTaskData.client_markup_percentage,
      });
      
      toast.success('Task updated successfully');
      setInlineTaskEditOpen(false);
      
      // Refresh BOQ items to show updated values
      if (selectedWorksheet) {
        openEditDialog(selectedWorksheet);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };
  
  // AI Description generation for inline task edit
  const generateInlineAIDescription = async () => {
    if (!inlineTaskData?.title) {
      toast.error('Please enter a task title first');
      return;
    }
    
    setInlineAiDescLoading(true);
    try {
      const categoryInfo = boqCategories.find(c => c.group === inlineTaskData.category_group);
      const subCat = categoryInfo?.categories?.find(cat => cat.code === inlineTaskData.category_code);
      
      const response = await axios.post(`${API}/tasks/ai-description`, {
        task_title: inlineTaskData.title,
        category_name: subCat?.name || categoryInfo?.group_name || '',
        project_type: 'Interior',
        keywords: ''
      });
      
      if (response.data.success) {
        setInlineTaskData({ ...inlineTaskData, description: response.data.description });
        toast.success(response.data.ai_assisted ? 'AI description generated!' : 'Template description added');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI description');
    } finally {
      setInlineAiDescLoading(false);
    }
  };
  
  // BOQ Item Drag-and-Drop handlers
  const handleItemDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };
  
  const handleItemDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItemIndex(null);
    setDragOverItemIndex(null);
  };
  
  const handleItemDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedItemIndex !== null && draggedItemIndex !== index) {
      setDragOverItemIndex(index);
    }
  };
  
  const handleItemDragLeave = () => {
    setDragOverItemIndex(null);
  };
  
  const handleItemDrop = (e, targetIndex) => {
    e.preventDefault();
    setDragOverItemIndex(null);
    
    if (draggedItemIndex === null || draggedItemIndex === targetIndex) return;
    
    const newItems = [...editItems];
    const [removed] = newItems.splice(draggedItemIndex, 1);
    newItems.splice(targetIndex, 0, removed);
    
    // Update display_order for all items
    const reorderedItems = newItems.map((item, idx) => ({
      ...item,
      display_order: idx + 1
    }));
    
    setEditItems(reorderedItems);
    toast.success('Item order updated');
  };
  
  // Toggle description expansion
  const toggleDescriptionExpand = (index) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Helper function to get rate based on selected layer
  const getRateForLayer = (worksheet) => {
    switch (rateLayer) {
      case 'vendor':
        return worksheet.vendor_total || worksheet.subtotal || 0;
      case 'client':
        return worksheet.client_total || 0;
      case 'admin':
      default:
        return worksheet.admin_total || worksheet.subtotal || 0;
    }
  };

  // Get item rate based on layer
  const getItemRateForLayer = (item) => {
    switch (rateLayer) {
      case 'vendor':
        return item.vendor_rate || item.rate || 0;
      case 'client':
        return item.client_rate || 0;
      case 'admin':
      default:
        return item.admin_rate || item.rate || 0;
    }
  };

  // iter-386u-h — task #5: download OTP access log (CSV/xlsx)
  const handleDownloadAccessLog = async (worksheetId, worksheetNumber, format = 'csv') => {
    try {
      const url = `${API}/boq-worksheets/${worksheetId}/access-log?format=${format}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const filename = `access-log-${worksheetNumber || worksheetId}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Access log downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download access log');
    }
  };

  // Export with authentication - supports role-based export for admin
  const handleExport = async (worksheetId, format, exportRole = null) => {    try {
      // Build URL with optional role parameter for admin exports
      let url = `${API}/boq-worksheets/${worksheetId}/export/${format}`;
      if (exportRole) {
        url += `?export_role=${exportRole}`;
      }
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Get worksheet number for filename
      const worksheet = worksheets.find(w => w.id === worksheetId);
      const roleLabel = exportRole ? `_${exportRole.toUpperCase()}` : '';
      const filename = `${worksheet?.worksheet_number || 'BOQ'}${roleLabel}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      const roleText = exportRole ? ` (${exportRole.charAt(0).toUpperCase() + exportRole.slice(1)} View)` : '';
      toast.success(`Exported ${filename}${roleText} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to export ${format.toUpperCase()}`);
    }
  };

  // Bulk GST update for all items
  const handleBulkGSTUpdate = (gstValue) => {
    const updated = editItems.map(item => ({
      ...item,
      gst_percentage: gstValue,
      gst_amount: item.amount * gstValue / 100,
      amount_with_gst: item.amount * (1 + gstValue / 100),
    }));
    setEditItems(updated);
    toast.success(`Applied ${gstValue}% GST to all ${updated.length} items`);
  };

  // Handle BOQ Confirmation (Mark as Confirmed/Rejected)
  const handleConfirmBOQ = async () => {
    if (!confirmWorksheet) return;
    setConfirming(true);
    try {
      const response = await axios.post(`${API}/boq-worksheets/${confirmWorksheet.id}/confirm`, {
        status: confirmStatus,
        notes: confirmNotes
      });
      
      // Update local state immediately to avoid needing a page refresh
      setWorksheets(prev => prev.map(w => 
        w.id === confirmWorksheet.id 
          ? { ...w, confirmation_status: confirmStatus }
          : w
      ));
      
      toast.success(`BOQ ${confirmWorksheet.worksheet_number} marked as ${confirmStatus}`);
      setConfirmDialogOpen(false);
      setConfirmWorksheet(null);
      setConfirmNotes('');
    } catch (error) {
      console.error('BOQ confirm error:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to update confirmation status';
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  // Fetch BOQ Summary
  const fetchSummary = async (worksheetId, viewRole = 'admin') => {
    setSummaryLoading(true);
    try {
      const response = await axios.get(`${API}/boq-worksheets/${worksheetId}/summary?view_role=${viewRole}`);
      setSummaryData(response.data);
      setSummaryDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load BOQ summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  // PHASE 2: Admin Tools handlers (relocated from Tasks page)
  const handleCloneBOQ = async (e) => {
    e.preventDefault();
    if (!cloneBOQData.source_project_id || !cloneBOQData.target_project_id) {
      toast.error('Please select both source and target projects'); return;
    }
    if (cloneBOQData.source_project_id === cloneBOQData.target_project_id) {
      toast.error('Source and target projects must be different'); return;
    }
    setCloneBOQLoading(true);
    try {
      await axios.post(`${API}/projects/${cloneBOQData.source_project_id}/clone-boq`, {
        target_project_id: cloneBOQData.target_project_id,
        include_descriptions: cloneBOQData.include_descriptions
      });
      toast.success('BOQ cloned successfully!');
      setCloneBOQDialogOpen(false);
      setCloneBOQData({ source_project_id: '', target_project_id: '', include_descriptions: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone BOQ');
    } finally { setCloneBOQLoading(false); }
  };

  const handleCreateVendorQuoteRequest = async (e) => {
    e.preventDefault();
    if (!vendorQuoteData.project_id || !vendorQuoteData.vendor_name) {
      toast.error('Project and vendor name are required'); return;
    }
    setVendorQuoteLoading(true);
    try {
      const response = await axios.post(`${API}/vendor-quote-requests`, {
        project_id: vendorQuoteData.project_id,
        vendor_name: vendorQuoteData.vendor_name,
        vendor_email: vendorQuoteData.vendor_email || undefined,
        instructions: vendorQuoteData.instructions || undefined,
        expiry_days: vendorQuoteData.expiry_days || 7,
      });
      const url = `${window.location.origin}/vendor-quote/${response.data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success('Quote link created & copied!'); }
      catch { toast.success(`Quote link created! URL: ${url}`); }
      setVendorQuoteDialogOpen(false);
      setVendorQuoteData({ project_id: '', vendor_name: '', vendor_email: '', instructions: '', expiry_days: 7 });
      fetchVendorQuoteRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote request');
    } finally { setVendorQuoteLoading(false); }
  };

  const fetchVendorQuoteRequests = async () => {
    try {
      const response = await axios.get(`${API}/vendor-quote-requests`);
      setVendorQuoteRequests(response.data);
    } catch { console.error('Failed to fetch vendor quote requests'); }
  };

  const handleDeleteVendorQuoteRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/vendor-quote-requests/${requestId}`);
      toast.success('Quote request deleted');
      fetchVendorQuoteRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };


  const handleDelete = async () => {
    if (!selectedWorksheet) return;
    try {
      await axios.delete(`${API}/boq-worksheets/${selectedWorksheet.id}`, {
        data: { reason: deleteReason }
      });
      toast.success('BOQ worksheet deleted');
      setDeleteDialogOpen(false);
      setSelectedWorksheet(null);
      setDeleteReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // iter-318: Toggle BOQ Archive/Active (admin only)
  const handleToggleBoqActive = async (worksheet, e) => {
    if (e) e.stopPropagation();
    const wasActive = worksheet.is_active !== false;
    try {
      const res = await axios.patch(`${API}/boq-worksheets/${worksheet.id}/toggle-active`);
      toast.success(`BOQ marked as ${res.data.is_active ? 'Active' : 'Archived'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleUnlock = async () => {
    if (!selectedWorksheet) return;
    if (!unlockReason || unlockReason.trim().length < 10) {
      toast.error('Please provide a detailed reason (minimum 10 characters)');
      return;
    }
    try {
      const response = await axios.post(`${API}/boq-worksheets/${selectedWorksheet.id}/unlock`, {
        reason: unlockReason
      });
      toast.success(response.data.message || 'BOQ unlocked successfully');
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      setUnlockDialogOpen(false);
      setSelectedWorksheet(null);
      setUnlockReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock BOQ');
    }
  };

  // Publish/Unpublish BOQ to client
  const openPublishDialog = (worksheet) => {
    setPublishWorksheet(worksheet);
    // Set default validity date to 15 days from now
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 15);
    setValidityDate(defaultDate.toISOString().split('T')[0]);
    setGeneratePublicLink(false);
    setPublishDialogOpen(true);
  };
  
  const handlePublishToClient = async () => {
    if (!publishWorksheet) return;
    
    setPublishing(true);
    try {
      const response = await axios.post(`${API}/boq-worksheets/${publishWorksheet.id}/publish-to-client`, {
        visible: true,
        validity_date: new Date(validityDate).toISOString(),
        generate_public_link: generatePublicLink
      });
      toast.success(response.data.message);
      
      // Show public link if generated
      if (response.data.public_link) {
        setCurrentPublicLink({
          url: `${window.location.origin}/public/boq/${response.data.public_token}`,
          token: response.data.public_token,
          expiresAt: response.data.validity_date
        });
        setPublicLinkDialogOpen(true);
      }
      
      setPublishDialogOpen(false);
      setPublishWorksheet(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish BOQ');
    } finally {
      setPublishing(false);
    }
  };
  
  const handleUnpublishBOQ = async (worksheet) => {
    try {
      const response = await axios.post(`${API}/boq-worksheets/${worksheet.id}/publish-to-client`, {
        visible: false
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpublish BOQ');
    }
  };
  
  // Publish to Vendor handlers
  const openPublishVendorDialog = (worksheet) => {
    setPublishVendorWorksheet(worksheet);
    setGenerateVendorPublicLink(false);
    setPublishVendorDialogOpen(true);
  };
  
  const handlePublishToVendor = async () => {
    if (!publishVendorWorksheet) return;
    
    setPublishingVendor(true);
    try {
      const response = await axios.post(`${API}/boq-worksheets/${publishVendorWorksheet.id}/publish-to-vendor`, {
        visible: true,
        validity_date: publishVendorWorksheet.validity_date || new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
        generate_public_link: generateVendorPublicLink
      });
      toast.success(response.data.message);
      
      // Show vendor public link if generated
      if (response.data.vendor_public_link) {
        setCurrentPublicLink({
          url: `${window.location.origin}/public/boq/${response.data.vendor_public_token}`,
          token: response.data.vendor_public_token,
          expiresAt: publishVendorWorksheet.validity_date,
          type: 'vendor'
        });
        setPublicLinkDialogOpen(true);
      }
      
      setPublishVendorDialogOpen(false);
      setPublishVendorWorksheet(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish BOQ to vendors');
    } finally {
      setPublishingVendor(false);
    }
  };
  
  const handleUnpublishFromVendor = async (worksheet) => {
    try {
      const response = await axios.post(`${API}/boq-worksheets/${worksheet.id}/publish-to-vendor`, {
        visible: false
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpublish from vendors');
    }
  };
  
  const handleGeneratePublicLink = async (worksheet) => {
    try {
      const response = await axios.post(`${API}/boq-worksheets/${worksheet.id}/generate-public-link`);
      setCurrentPublicLink({
        url: `${window.location.origin}/public/boq/${response.data.public_token}`,
        token: response.data.public_token,
        expiresAt: response.data.expires_at
      });
      setPublicLinkDialogOpen(true);
      toast.success('Public link generated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate public link');
    }
  };
  
  const handleRevokePublicLink = async (worksheet) => {
    try {
      await axios.post(`${API}/boq-worksheets/${worksheet.id}/revoke-public-link`);
      toast.success('Client public link revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke public link');
    }
  };
  
  // Vendor Public Link Handlers
  const handleGenerateVendorPublicLink = async (worksheet) => {
    try {
      const response = await axios.post(`${API}/boq-worksheets/${worksheet.id}/generate-vendor-public-link`);
      setCurrentPublicLink({
        url: `${window.location.origin}/public/boq/${response.data.vendor_public_token}`,
        token: response.data.vendor_public_token,
        expiresAt: response.data.expires_at,
        type: 'vendor'
      });
      setPublicLinkDialogOpen(true);
      toast.success('Vendor public link generated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate vendor public link');
    }
  };
  
  const handleRevokeVendorPublicLink = async (worksheet) => {
    try {
      await axios.post(`${API}/boq-worksheets/${worksheet.id}/revoke-vendor-public-link`);
      toast.success('Vendor public link revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke vendor public link');
    }
  };
  
  const handleExtendValidity = async (worksheet, newDate) => {
    try {
      const response = await axios.post(`${API}/boq-worksheets/${worksheet.id}/extend-validity`, {
        new_validity_date: new Date(newDate).toISOString()
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend validity');
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const openEditDialog = (worksheet) => {
    setSelectedWorksheet(worksheet);
    // Preserve GST percentage from saved data - use the stored value, not default to 0
    setEditItems(worksheet.items.map((item, idx) => ({
      ...item,
      _editId: item.id || item.task_id || `edit_${idx}_${Date.now()}`,
      gst_percentage: item.gst_percentage !== undefined ? item.gst_percentage : 0,
      gst_amount: item.gst_amount || 0,
      amount_with_gst: item.amount_with_gst || item.amount || 0,
    })));
    // Load saved discount settings
    setDiscountType(worksheet.discount_type || 'value');
    setDiscountValue(worksheet.discount_value || 0);
    setEditDialogOpen(true);
    // Load BOQ categories for inline editing
    fetchBoqCategories();
    // Load additional items summary for Final Total
    fetchAdditionalItemsSummary(worksheet.id);
  };

  const fetchAdditionalItemsSummary = async (worksheetId) => {
    try {
      const response = await axios.get(`${API}/boq-worksheets/${worksheetId}/additional-items-summary`, {
        headers: { Authorization: `Bearer ${user.token || localStorage.getItem('token')}` }
      });
      setAdditionalItemsSummary(response.data);
    } catch {
      setAdditionalItemsSummary(null);
    }
  };

  const handleSaveFinalTotal = async () => {
    if (!selectedWorksheet) return;
    setSavingFinalTotal(true);
    try {
      const response = await axios.post(`${API}/boq-worksheets/${selectedWorksheet.id}/save-final-total`, {}, {
        headers: { Authorization: `Bearer ${user.token || localStorage.getItem('token')}` }
      });
      toast.success(response.data.message);
      setAdditionalItemsSummary(prev => ({
        ...prev,
        saved_final_total: response.data.final_total,
        saved_additional_items_total: response.data.additional_items_total
      }));
      // Refresh the worksheet list and history
      fetchData();
      if (showHistory) fetchFinalTotalHistory(selectedWorksheet.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Final Total');
    } finally {
      setSavingFinalTotal(false);
    }
  };

  const fetchFinalTotalHistory = async (worksheetId) => {
    try {
      const response = await axios.get(`${API}/boq-worksheets/${worksheetId}/final-total-history`, {
        headers: { Authorization: `Bearer ${user.token || localStorage.getItem('token')}` }
      });
      setFinalTotalHistory(response.data || []);
    } catch {
      setFinalTotalHistory([]);
    }
  };

  const handleItemChange = (index, field, value) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const newItem = { ...item, [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const pct1 = selectedWorksheet?.admin_markup_percentage || 20;
        const pct2 = selectedWorksheet?.client_markup_percentage || 20;
        if (isCtV(selectedWorksheet)) {
          // Client→Vendor: rate field IS client_rate, derive via markdown
          const clientRate = newItem.rate || 0;
          newItem.client_rate = clientRate;
          newItem.admin_rate = Math.round(clientRate * (1 - pct1 / 100) * 100) / 100;
          newItem.vendor_rate = Math.round(newItem.admin_rate * (1 - pct2 / 100) * 100) / 100;
          newItem.amount = (newItem.quantity || 0) * clientRate;
        } else {
          // Vendor→Client: rate field IS vendor_rate, derive via markup
          const vendorRate = newItem.rate || 0;
          newItem.vendor_rate = vendorRate;
          newItem.admin_rate = Math.round(vendorRate * (1 + pct1 / 100) * 100) / 100;
          newItem.client_rate = Math.round(newItem.admin_rate * (1 + pct2 / 100) * 100) / 100;
          newItem.amount = (newItem.quantity || 0) * vendorRate;
        }
        if (newItem.gst_percentage) {
          newItem.gst_amount = newItem.amount * newItem.gst_percentage / 100;
          newItem.amount_with_gst = newItem.amount + newItem.gst_amount;
        }
      }
      if (field === 'gst_percentage') {
        newItem.gst_amount = (newItem.amount || 0) * value / 100;
        newItem.amount_with_gst = (newItem.amount || 0) + newItem.gst_amount;
      }
      return newItem;
    }));
  };

  // MODEL-AWARE: base rate input — vendor_rate for V→C, client_rate for C→V
  const handleBaseRateChange = (index, rateValue) => {
    const pct1 = selectedWorksheet?.admin_markup_percentage || 20;
    const pct2 = selectedWorksheet?.client_markup_percentage || 20;
    let vendorRate, adminRate, clientRate, baseAmount;
    if (isCtV(selectedWorksheet)) {
      clientRate = rateValue;
      adminRate = Math.round(clientRate * (1 - pct1 / 100) * 100) / 100;
      vendorRate = Math.round(adminRate * (1 - pct2 / 100) * 100) / 100;
    } else {
      vendorRate = rateValue;
      adminRate = Math.round(vendorRate * (1 + pct1 / 100) * 100) / 100;
      clientRate = Math.round(adminRate * (1 + pct2 / 100) * 100) / 100;
    }
    setEditItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const newItem = { ...item };
      newItem.rate = rateValue;
      newItem.vendor_rate = vendorRate;
      newItem.admin_rate = adminRate;
      newItem.client_rate = clientRate;
      newItem.amount = (newItem.quantity || 0) * rateValue;
      if (newItem.gst_percentage) {
        newItem.gst_amount = newItem.amount * newItem.gst_percentage / 100;
        newItem.amount_with_gst = newItem.amount + newItem.gst_amount;
      }
      return newItem;
    }));
  };

  const handleItemBatchChange = (index, changes) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const newItem = { ...item, ...changes };
      if ('quantity' in changes || 'rate' in changes) {
        newItem.amount = (newItem.quantity || 0) * (newItem.rate || 0);
        if (newItem.gst_percentage) {
          newItem.gst_amount = newItem.amount * newItem.gst_percentage / 100;
          newItem.amount_with_gst = newItem.amount + newItem.gst_amount;
        }
      }
      return newItem;
    }));
  };

  const handleAddInlineRow = () => {
    const newRow = {
      _editId: `new_${Date.now()}_${Math.random().toString(36).substr(2,5)}`,
      id: '',
      task_id: '',
      task_title: '',
      description: '',
      quantity: 1,
      unit: 'No.s',
      rate: 0,
      amount: 0,
      gst_percentage: 0,
      gst_amount: 0,
      amount_with_gst: 0,
      main_category_id: '',
      main_category_code: '',
      main_category_name: '',
      sub_category_id: '',
      sub_category_code: '',
      sub_category_name: '',
      category_group: '',
      category_name: '',
      is_new_inline: true,
    };
    setEditItems(prev => [...prev, newRow]);
  };

  const handleAddHeaderRow = () => {
    const headerName = prompt('Enter header name (e.g., "Master Bedroom", "Living Room"):');
    if (!headerName?.trim()) return;
    const newHeader = {
      _editId: `header_${Date.now()}_${Math.random().toString(36).substr(2,5)}`,
      id: '',
      item_type: 'header',
      task_title: headerName.trim(),
      description: '',
      quantity: 0,
      unit: '',
      rate: 0,
      amount: 0,
      gst_percentage: 0,
      main_category_id: '',
      main_category_code: '',
      main_category_name: '',
      sub_category_id: '',
      sub_category_code: '',
      sub_category_name: '',
      category_group: '',
      category_name: '',
      is_new_inline: true,
    };
    setEditItems(prev => [...prev, newHeader]);
  };

  // Build category color map from boqCategories
  const categoryColorMap = useMemo(() => {
    const map = {};
    (boqCategories || []).forEach(cat => {
      if (cat.color) {
        map[cat.group || cat.code] = cat.color;
        if (cat.main_category_id) map[cat.main_category_id] = cat.color;
      }
    });
    return map;
  }, [boqCategories]);

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      await axios.put(`${API}/boq-worksheets/${selectedWorksheet.id}`, {
        items: editItems.map(item => ({
          ...item,
          sync_to_task: true,
        })),
        discount_type: discountType,
        discount_value: discountValue,
        discount_percentage: discountType === 'percentage' ? discountValue : 0,
      });
      toast.success('BOQ updated and tasks synced');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteItem = (index) => {
    const item = editItems[index];
    // Headers can be directly removed (no linked task)
    if (item.item_type === 'header') {
      setEditItems(prev => prev.filter((_, i) => i !== index));
      toast.success('Header removed');
      return;
    }
    setItemToDelete({ index, item });
    setDeleteAction('unlink');
    setDeleteItemDialogOpen(true);
  };

  const confirmDeleteItem = async () => {
    if (itemToDelete === null) return;
    const item = itemToDelete.item;
    
    if (selectedWorksheet && item.task_id) {
      try {
        await axios.post(`${API}/boq-worksheets/${selectedWorksheet.id}/remove-line-item`, {
          task_id: item.task_id,
          action: deleteAction,
          reason: deleteReason,
        });
        const updated = editItems.filter((_, idx) => idx !== itemToDelete.index);
        setEditItems(updated);
        setDeleteItemDialogOpen(false);
        setItemToDelete(null);
        setDeleteReason('');
        toast.success(deleteAction === 'delete' ? 'Line item and task deleted' : 'Line item removed, task unlinked');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to remove item');
      }
    } else {
      const updated = editItems.filter((_, idx) => idx !== itemToDelete.index);
      setEditItems(updated);
      setDeleteItemDialogOpen(false);
      setItemToDelete(null);
      toast.success('Line item removed');
    }
  };

  const handleAddLineItem = async () => {
    if (!newItemData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!newItemData.main_category_id) {
      toast.error('Please select a Main Category');
      return;
    }
    try {
      await axios.post(`${API}/boq-worksheets/${selectedWorksheet.id}/add-line-item`, newItemData);
      toast.success('Line item added and task created');
      setAddItemDialogOpen(false);
      setNewItemData({ 
        title: '', description: '', quantity: 1, unit: 'No.s', rate: 0, gst_percentage: 18,
        main_category_id: '', main_category_code: '', main_category_name: '',
        sub_category_id: '', sub_category_code: '', sub_category_name: '',
      });
      fetchData();
      // Refresh edit items
      const res = await axios.get(`${API}/boq-worksheets`);
      const updated = res.data.find(w => w.id === selectedWorksheet.id);
      if (updated) {
        setEditItems(updated.items.map(item => ({ ...item, gst_percentage: item.gst_percentage || 0 })));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add line item');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  // iter-338z8: create an empty BOQ then land on Edit BOQ.
  const handleCreateEmptyBOQ = async () => {
    if (!newBoqProjectId) { toast.error('Pick a project'); return; }
    setCreatingNewBoq(true);
    try {
      const r = await axios.post(
        `${API}/boq-worksheets/create-empty`,
        { project_id: newBoqProjectId, display_name: newBoqDisplayName.trim() || null },
      );
      toast.success(`Created ${r.data?.worksheet_number} — add line items now`);
      setNewBoqDialogOpen(false);
      setNewBoqProjectId('');
      setNewBoqDisplayName('');
      navigate(`/boq-worksheets/${r.data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create BOQ');
    } finally {
      setCreatingNewBoq(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="boq-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-2">
              BOQ Worksheets
            </h1>
            <p className="text-stone-500">Bill of Quantities grouped by project</p>
          </div>
          {isAuthorized && (
            <Button
              onClick={() => setNewBoqDialogOpen(true)}
              className="bg-stone-900 hover:bg-stone-800 text-white shrink-0"
              data-testid="new-boq-btn"
            >
              <Plus size={16} className="mr-1.5" />
              New BOQ
            </Button>
          )}
        </div>

        {/* Filter by Project + Active/Archived (iter-318) */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-full md:w-64" data-testid="boq-project-filter-select">
              <SelectValue placeholder="Filter by project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {[...tabProjects]
                .sort((a, b) => {
                  const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                  const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                  return dateB - dateA;
                })
                .map((p, idx) => <SelectItem key={p.id} value={p.id} data-testid={`boq-project-option-${p.id}`}>{idx + 1}. {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          
          {/* iter-318: Archive/Inactive filter chips (admin only) */}
          <div className="inline-flex items-center rounded-md border border-stone-300 overflow-hidden text-xs" data-testid="boq-active-filter">
            {[
              { v: 'active', label: 'Active' },
              { v: 'inactive', label: 'Archived' },
              { v: 'all', label: 'All' },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setActiveFilter(opt.v)}
                className={`px-3 py-1.5 font-medium transition-colors ${activeFilter === opt.v ? 'bg-stone-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}
                data-testid={`boq-active-filter-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Rate toggle hint */}
          {['owner', 'staff', 'admin', 'project_manager'].includes(user.role) && (
            <div className="flex items-center gap-2 text-xs text-stone-400">
              <Layers size={14} />
              <span>Rate toggle available on each project group header (V/A/C)</span>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <ClipboardList size={20} className="text-stone-800" />
              <span className="text-2xl font-bold">{totalBOQs}</span>
            </div>
            <p className="text-sm text-stone-500">Total BOQs</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <FileText size={20} className="text-stone-600" />
              <span className="text-2xl font-bold">{pendingBOQs}</span>
            </div>
            <p className="text-sm text-stone-500">Pending</p>
          </div>
          <div className="apple-card p-4 border-green-200">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle size={20} className="text-green-600" />
              <span className="text-2xl font-bold text-green-700">{confirmedBOQs}</span>
            </div>
            <p className="text-sm text-green-600">Confirmed</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <Lock size={20} className="text-purple-500" />
              <span className="text-2xl font-bold">{lockedBOQs}</span>
            </div>
            <p className="text-sm text-stone-500">Quoted (Locked)</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <Receipt size={20} className="text-green-500" />
              <span className="text-2xl font-bold">{invoicedBOQs}</span>
            </div>
            <p className="text-sm text-stone-500">Invoiced</p>
          </div>
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-lg font-bold">₹</span>
              <span className="text-2xl font-bold">{(totalValue / 100000).toFixed(1)}L</span>
            </div>
            <p className="text-sm text-stone-500">Total Value ({rateLayer === 'vendor' ? 'V' : rateLayer === 'admin' ? 'A' : 'C'} · {useActualValues ? 'Site' : 'Est'})</p>
          </div>
        </div>

        {/* PHASE 2: Admin Tools - relocated from Tasks page */}
        {(user.role === 'owner' || user.role === 'staff' || user.role === 'admin') && (
          <div className="flex items-center gap-2 mb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2 text-stone-700 border-stone-300 hover:bg-stone-50" data-testid="admin-tools-button">
                  <Settings2 size={18} />
                  Admin Tools
                  <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => setCloneBOQDialogOpen(true)} className="cursor-pointer" data-testid="clone-boq-menu-item">
                  <Copy size={16} className="mr-2 text-blue-600" />
                  <span>Clone BOQ to Project</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setVendorQuoteDialogOpen(true)} className="cursor-pointer" data-testid="create-vendor-quote-menu-item">
                  <Link size={16} className="mr-2 text-green-600" />
                  <span>Create Vendor Quote Link</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { fetchVendorQuoteRequests(); setViewQuoteRequestsDialogOpen(true); }} className="cursor-pointer" data-testid="view-vendor-quotes-menu-item">
                  <ClipboardCheck size={16} className="mr-2 text-amber-600" />
                  <span>View Quote Requests ({vendorQuoteRequests.length})</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* BOQ List Grouped by Project */}
        {filteredWorksheets.length === 0 ? (
          <div className="apple-card p-16 text-center">
            <ClipboardList size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">No BOQ worksheets found</p>
            <p className="text-sm text-stone-400 mt-1">Create BOQ from approved tasks in the Tasks page</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedByProject)
              .sort((a, b) => {
                const projA = projects.find(p => p.id === a);
                const projB = projects.find(p => p.id === b);
                return (projB?.created_at || '').localeCompare(projA?.created_at || '');
              })
              .map((projectId) => (
              <div key={projectId} className="apple-card">
                {/* Project Header with Inline Rate Toggle */}
                <div className="bg-stone-50 px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    {isAuthorized && (() => {
                      const projBoqIds = groupedByProject[projectId].map(w => w.id);
                      const selectedInProj = projBoqIds.filter(id => selectedBoqIds.has(id)).length;
                      const allSelected = projBoqIds.length > 0 && selectedInProj === projBoqIds.length;
                      const someSelected = selectedInProj > 0 && !allSelected;
                      return (
                        <Checkbox
                          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                          onCheckedChange={(v) => setProjectSelection(projBoqIds, v === true || v === 'indeterminate' ? !allSelected : !allSelected)}
                          data-testid={`bulk-select-all-${projectId}`}
                          aria-label="Select all BOQs in this project"
                          title={allSelected ? 'Clear all' : 'Select all BOQs in this project'}
                        />
                      );
                    })()}
                    <h3 className="font-semibold text-stone-900">{getProjectName(projectId)}</h3>
                    <span className="text-sm text-stone-500">{groupedByProject[projectId].length} BOQ(s)</span>
                    {/* Pricing model indicator */}
                    {groupedByProject[projectId][0]?.pricing_model === 'client_to_vendor' ? (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium" data-testid={`pricing-model-badge-${projectId}`}>C→V</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-500 rounded font-medium" data-testid={`pricing-model-badge-${projectId}`}>V→C</span>
                    )}
                    {isAuthorized && (
                      <button
                        onClick={() => { setCompareProjectId(projectId); setCompareDialogOpen(true); }}
                        className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded font-medium hover:bg-amber-100 transition-colors"
                        data-testid={`compare-models-btn-${projectId}`}
                      >
                        Compare Models
                      </button>
                    )}
                  </div>
                  
                  {/* Inline Rate View Toggle - Moved here from page top */}
                  {['owner', 'staff', 'admin', 'project_manager'].includes(user.role) && (
                    <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-stone-200">
                      <Layers size={14} className="text-stone-400 ml-1" />
                      <Tabs value={rateLayer} onValueChange={setRateLayer} className="h-7">
                        <TabsList className="h-7 bg-transparent p-0">
                          <TabsTrigger 
                            value="vendor" 
                            className={`h-6 px-2 text-xs font-medium data-[state=active]:bg-purple-500 data-[state=active]:text-white`}
                            data-testid={`rate-layer-vendor-${projectId}`}
                          >
                            V
                          </TabsTrigger>
                          <TabsTrigger 
                            value="admin" 
                            className={`h-6 px-2 text-xs font-medium data-[state=active]:bg-stone-700 data-[state=active]:text-white`}
                            data-testid={`rate-layer-admin-${projectId}`}
                          >
                            A
                          </TabsTrigger>
                          <TabsTrigger 
                            value="client" 
                            className={`h-6 px-2 text-xs font-medium data-[state=active]:bg-blue-500 data-[state=active]:text-white`}
                            data-testid={`rate-layer-client-${projectId}`}
                          >
                            C
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                      <span className="text-xs text-stone-400 mr-1 hidden sm:inline">
                        {rateLayer === 'vendor' ? 'Vendor' : rateLayer === 'admin' ? 'Admin' : 'Client'}
                      </span>
                      {/* iter-313: Actual (Site) / Estimated (Design) sub-toggle */}
                      <span className="text-xs text-stone-300">|</span>
                      <Tabs value={useActualValues ? 'actual' : 'estimated'} onValueChange={(v) => setUseActualValues(v === 'actual')} className="h-7">
                        <TabsList className="h-7 bg-transparent p-0">
                          <TabsTrigger
                            value="estimated"
                            className="h-6 px-2 text-[10px] font-medium data-[state=active]:bg-stone-200 data-[state=active]:text-stone-800"
                            data-testid={`value-mode-estimated-${projectId}`}
                            title="Estimated (Design) — original BOQ quantities & rates"
                          >
                            Est.
                          </TabsTrigger>
                          <TabsTrigger
                            value="actual"
                            className="h-6 px-2 text-[10px] font-medium data-[state=active]:bg-green-500 data-[state=active]:text-white"
                            data-testid={`value-mode-actual-${projectId}`}
                            title="Actual (Site) — site-corrected quantities & rates"
                          >
                            Site
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>
                  )}
                </div>
                
                {/* BOQ Items */}
                <div className="divide-y">
                  {groupedByProject[projectId].map((worksheet) => {
                    const isInvoiced = hasInvoice(worksheet.id);
                    const invoiceNum = getInvoiceNumber(worksheet.id);
                    const isLocked = worksheet.is_locked;
                    const isSelected = selectedBoqIds.has(worksheet.id);
                    
                    return (
                      <div key={worksheet.id} data-testid={`boq-row-${worksheet.id}`} data-rejected={isRejectedBoq(worksheet) ? 'true' : 'false'} className={`p-4 md:p-6 transition-colors group ${isSelected ? 'bg-blue-50/70 ring-1 ring-inset ring-blue-200' : isRejectedBoq(worksheet) ? 'bg-stone-100/90 hover:bg-stone-100 [&_h4]:text-stone-400 [&_.text-stone-900]:text-stone-400 [&_.text-stone-500]:text-stone-400' : 'hover:bg-stone-50'}`}>
                        {/* Clean 2-row layout to prevent clutter */}
                        <div className="flex gap-3">
                          {/* Bulk select checkbox */}
                          {isAuthorized && (
                            <div className="pt-1 flex-shrink-0">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleBoqSelected(worksheet.id)}
                                data-testid={`bulk-select-boq-${worksheet.id}`}
                                aria-label={`Select BOQ ${worksheet.worksheet_number}`}
                              />
                            </div>
                          )}
                          <div className="flex flex-col gap-3 flex-1 min-w-0">
                          {/* Row 1: BOQ Number, Status badges, Date */}
                          <div className="flex flex-wrap items-center gap-2">
                            {worksheet.display_name ? (
                              <>
                                <h4 className="font-semibold text-stone-900" data-testid={`boq-display-name-${worksheet.id}`}>{worksheet.display_name}</h4>
                                <span className="text-xs text-stone-500 font-mono">{worksheet.worksheet_number}</span>
                              </>
                            ) : (
                              <h4 className="font-semibold text-stone-900">{worksheet.worksheet_number}</h4>
                            )}
                            {worksheet.is_active === false && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-stone-200 text-stone-600 flex items-center gap-1" data-testid={`boq-archived-badge-${worksheet.id}`}>
                                <Archive size={12} />
                                Archived
                              </span>
                            )}
                            {isLocked && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1" title={`Locked: ${worksheet.locked_reason || 'Quotation generated'}`}>
                                <Lock size={12} />
                                Locked
                              </span>
                            )}
                            {isInvoiced ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                                <CheckCircle size={12} />
                                Invoiced ({invoiceNum})
                              </span>
                            ) : !isLocked && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-700">
                                Pending
                              </span>
                            )}
                            {worksheet.linked_quotation_number && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                {worksheet.linked_quotation_number}
                              </span>
                            )}
                            {/* BOQ Confirmation Status Badge */}
                            {worksheet.confirmation_status === 'confirmed' ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                                <CheckCircle size={12} />
                                Confirmed
                              </span>
                            ) : worksheet.confirmation_status === 'rejected' ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white flex items-center gap-1" data-testid={`boq-rejected-badge-${worksheet.id}`}>
                                <X size={12} />
                                Rejected
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                                <Clock size={12} />
                                Pending
                              </span>
                            )}
                            {/* BOQ Confirmation Action Button - Owner/Admin only, always visible */}
                            {['owner', 'admin'].includes(user.role) && (
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation();
                                  setConfirmWorksheet(worksheet); 
                                  setConfirmStatus(worksheet.confirmation_status === 'confirmed' ? 'pending' : 'confirmed');
                                  setConfirmDialogOpen(true); 
                                }}
                                className={`px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all border shadow-sm ${
                                  worksheet.confirmation_status === 'confirmed' 
                                    ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100 hover:shadow'
                                    : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:shadow'
                                }`}
                                title={worksheet.confirmation_status === 'confirmed' 
                                  ? 'BOQ is confirmed for fund tracking - click to change'
                                  : 'Mark BOQ as Confirmed for fund tracking'}
                                data-testid={`confirm-boq-${worksheet.id}`}
                              >
                                {worksheet.confirmation_status === 'confirmed' ? (
                                  <><CheckCircle size={13} />Confirmed</>
                                ) : (
                                  <><ClipboardCheck size={13} />Confirm</>
                                )}
                              </button>
                            )}
                            <span className="flex items-center gap-1 text-xs text-stone-400 ml-auto">
                              <Calendar size={12} />
                              {formatDate(worksheet.created_at)}
                            </span>
                          </div>
                          
                          {/* Row 2: Items count, Selected Rate Value, and Published Status */}
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <span className="text-stone-500">{worksheet.items.length} items</span>
                            
                            {/* Single Rate Display Based on Selected Layer */}
                            {['owner', 'staff', 'admin', 'project_manager'].includes(user.role) && (
                              <span className={`font-bold text-lg ${
                                rateLayer === 'vendor' ? 'text-purple-600' :
                                rateLayer === 'admin' ? 'text-stone-700' :
                                'text-blue-600'
                              }`}>
                                ₹{getGrandTotal(worksheet, rateLayer, useActualValues).toLocaleString()}
                              </span>
                            )}
                            {!['owner', 'staff', 'admin', 'project_manager'].includes(user.role) && (
                              <span className="font-bold text-lg text-stone-900">₹{getGrandTotal(worksheet, 'client', useActualValues).toLocaleString()}</span>
                            )}
                            
                            {/* Final Total Badge */}
                            {worksheet.final_total && (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-medium" data-testid={`final-total-badge-${worksheet.id}`}>
                                Final: ₹{worksheet.final_total.toLocaleString()}
                              </span>
                            )}
                            
                            {/* View Counts - compact display */}
                            {(worksheet.client_visible || worksheet.vendor_public_token) && (
                              <div className="flex items-center gap-3 text-xs text-stone-400" data-testid={`view-counts-${worksheet.id}`}>
                                {worksheet.client_view_count > 0 && (
                                  <span className="flex items-center gap-1" title={`Client last viewed: ${worksheet.client_last_viewed_at ? new Date(worksheet.client_last_viewed_at).toLocaleString() : 'Never'}`}>
                                    <Eye size={11} className="text-blue-500" />
                                    {worksheet.client_view_count}
                                  </span>
                                )}
                                {worksheet.vendor_view_count > 0 && (
                                  <span className="flex items-center gap-1" title={`Vendor last viewed: ${worksheet.vendor_last_viewed_at ? new Date(worksheet.vendor_last_viewed_at).toLocaleString() : 'Never'}`}>
                                    <Eye size={11} className="text-purple-500" />
                                    {worksheet.vendor_view_count}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Row 3: Payment Schedule */}
                          <div className="pt-2 border-t border-stone-100">
                            <PaymentSchedule
                              worksheetId={worksheet.id}
                              totalValue={getGrandTotal(worksheet, 'client')}
                              isLocked={worksheet.is_locked}
                            />
                          </div>
                          
                          {/* Row 3b: Payment Tracker */}
                          <div className="pt-2">
                            <PaymentTracker worksheetId={worksheet.id} />
                          </div>
                          
                          {/* Row 4: Action Buttons - flex-wrap to prevent overflow */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100">
                            {/* View Summary Button */}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => fetchSummary(worksheet.id, 'client')}
                              disabled={summaryLoading}
                              data-testid={`view-summary-${worksheet.id}`}
                            >
                              <LayoutDashboard size={14} className="mr-1" />
                              Summary
                            </Button>
                            
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.open(`/boq-worksheets/${worksheet.id}/edit`, '_blank')}
                              disabled={isInvoiced || isLocked}
                              title={isLocked ? 'BOQ is locked - quotation has been generated' : ''}
                              data-testid={`edit-boq-${worksheet.id}`}
                            >
                              <ExternalLink size={14} className="mr-1" />
                              Edit
                            </Button>
                            
                            <DropdownMenu modal={true}>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid={`export-btn-${worksheet.id}`}>
                                  <Download size={14} className="mr-1" />
                                  Export
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" side="top" sideOffset={8} className="w-56 z-[9999]" collisionPadding={20} avoidCollisions={true}>
                                {/* PDF Export Options */}
                                <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                  PDF Downloads
                                </div>
                                <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(worksheet.id, 'pdf', 'vendor')}>
                                  <Users className="mr-2 text-purple-600" size={14} />
                                  <div>
                                    <div className="font-medium">Vendor BOQ</div>
                                    <div className="text-xs text-stone-500">{isCtV(worksheet) ? `Markdown −${worksheet.client_markup_percentage || 20}% on admin` : 'Base vendor rates'}</div>
                                  </div>
                                {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(worksheet.id, 'pdf', 'admin')}>
                                  <Building2 className="mr-2 text-stone-600" size={14} />
                                  <div>
                                    <div className="font-medium">Admin BOQ</div>
                                    <div className="text-xs text-stone-500">{isCtV(worksheet) ? `Markdown −${worksheet.admin_markup_percentage || 20}% on client` : `Markup +${worksheet.admin_markup_percentage || 20}% on vendor`}</div>
                                  </div>
                                {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(worksheet.id, 'pdf', 'client')}>
                                  <UserCircle className="mr-2 text-blue-600" size={14} />
                                  <div>
                                    <div className="font-medium">Client BOQ</div>
                                    <div className="text-xs text-stone-500">{isCtV(worksheet) ? 'Base client rates' : `Markup +${worksheet.client_markup_percentage || 20}% on admin`}</div>
                                  </div>
                                {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                                
                                <div className="my-1 border-t border-stone-200" />
                                
                                {/* Excel Export Options */}
                                <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                  Excel Downloads
                                </div>
                                <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(worksheet.id, 'excel', 'admin')}>
                                  <FileSpreadsheet className="mr-2 text-stone-600" size={14} />
                                  <div>
                                    <div className="font-medium">Admin Excel</div>
                                    <div className="text-xs text-stone-500">Full data export</div>
                                  </div>
                                {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(worksheet.id, 'excel', 'vendor')}>
                                  <FileSpreadsheet className="mr-2 text-purple-600" size={14} />
                                  <div>
                                    <div className="font-medium">Vendor Excel</div>
                                    <div className="text-xs text-stone-500">Vendor rates only</div>
                                  </div>
                                {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                                <DropdownMenuItem className={exportLocked ? 'opacity-50' : undefined} onClick={() => guardExport(handleExport)(worksheet.id, 'excel', 'client')}>
                                  <FileSpreadsheet className="mr-2 text-blue-600" size={14} />
                                  <div>
                                    <div className="font-medium">Client Excel</div>
                                    <div className="text-xs text-stone-500">Client rates only</div>
                                  </div>
                                {exportLocked && <Lock size={12} className="ml-auto text-stone-400" data-testid="export-lock-icon" />}
                                </DropdownMenuItem>
                                {/* iter-386u-h — task #5: OTP access-log download (admin only) */}
                                <DropdownMenuItem
                                  onClick={() => handleDownloadAccessLog(worksheet.id, worksheet.worksheet_number, 'csv')}
                                  data-testid={`download-access-log-csv-${worksheet.id}`}
                                >
                                  <ClipboardCheck className="mr-2 text-emerald-600" size={14} />
                                  <div>
                                    <div className="font-medium">Access Log (CSV)</div>
                                    <div className="text-xs text-stone-500">OTP verifications on public link</div>
                                  </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDownloadAccessLog(worksheet.id, worksheet.worksheet_number, 'xlsx')}
                                  data-testid={`download-access-log-xlsx-${worksheet.id}`}
                                >
                                  <ClipboardCheck className="mr-2 text-emerald-600" size={14} />
                                  <div>
                                    <div className="font-medium">Access Log (Excel)</div>
                                    <div className="text-xs text-stone-500">OTP verifications, .xlsx</div>
                                  </div>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            
                            {/* P0 FIX: Hide admin-only actions from vendors */}
                            {!isInvoiced && !isLocked && ['owner', 'admin', 'staff'].includes(user.role) && (
                              <>
                                {/* Purchase Order button - for Vendor Published items */}
                                {worksheet.vendor_visible && (
                                  <Button 
                                    size="sm"
                                    variant="outline"
                                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    onClick={() => handleCreatePurchaseOrder(worksheet.id)}
                                    title="Create Purchase Order for vendor-assigned items"
                                  >
                                    <ShoppingCart size={14} className="mr-1" />
                                    PO
                                  </Button>
                                )}
                                <Button 
                                  size="sm"
                                  className="apple-button"
                                  onClick={() => handleConvertToQuotation(worksheet.id)}
                                  title="Convert to Client Quotation (uses Actual/Site values)"
                                >
                                  <ArrowRight size={14} className="mr-1" />
                                  Quotation
                                </Button>
                                {/* Publish to Client - Enhanced with Validity Control */}
                                {worksheet.client_visible ? (
                                  <DropdownMenu modal={true}>
                                    <DropdownMenuTrigger asChild>
                                      <Button 
                                        size="sm"
                                        variant="outline"
                                        className={`${worksheet.status === 'expired' ? 'text-red-600 border-red-200' : 'text-green-600 border-green-200'}`}
                                      >
                                        <Share2 size={14} className="mr-1" />
                                        {worksheet.status === 'expired' ? 'Expired' : 'Published'}
                                        {worksheet.validity_date && (
                                          <span className="ml-1 text-xs">
                                            ({new Date(worksheet.validity_date) > new Date() 
                                              ? `${Math.ceil((new Date(worksheet.validity_date) - new Date()) / (1000 * 60 * 60 * 24))}d left`
                                              : 'Expired'})
                                          </span>
                                        )}
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent side="top" align="end" sideOffset={8} className="w-56 z-[9999]" collisionPadding={20} avoidCollisions={true}>
                                      <div className="px-3 py-2 text-xs text-stone-500">
                                        <div className="flex items-center gap-2">
                                          <CalendarClock size={14} />
                                          Valid until: {worksheet.validity_date 
                                            ? formatDate(worksheet.validity_date, { shortMonth: true })
                                            : 'Not set'}
                                        </div>
                                      </div>
                                      <div className="border-t border-stone-100" />
                                      
                                      {/* Client Public Link Options */}
                                      <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                        Client Link
                                      </div>
                                      {worksheet.public_token ? (
                                        <>
                                          <DropdownMenuItem onClick={() => {
                                            setCurrentPublicLink({
                                              url: `${window.location.origin}/public/boq/${worksheet.public_token}`,
                                              token: worksheet.public_token,
                                              expiresAt: worksheet.validity_date,
                                              type: 'client'
                                            });
                                            setPublicLinkDialogOpen(true);
                                          }}>
                                            <Eye size={14} className="mr-2 text-blue-600" />
                                            View Client Link
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleRevokePublicLink(worksheet)}>
                                            <Link2Off size={14} className="mr-2 text-red-600" />
                                            Revoke Client Link
                                          </DropdownMenuItem>
                                        </>
                                      ) : (
                                        <DropdownMenuItem onClick={() => handleGeneratePublicLink(worksheet)}>
                                          <Link size={14} className="mr-2 text-blue-600" />
                                          Generate Client Link
                                        </DropdownMenuItem>
                                      )}
                                      
                                      {/* Vendor Public Link Options */}
                                      <div className="border-t border-stone-100 mt-1" />
                                      <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                        Vendor Link
                                      </div>
                                      {worksheet.vendor_public_token ? (
                                        <>
                                          <DropdownMenuItem onClick={() => {
                                            setCurrentPublicLink({
                                              url: `${window.location.origin}/public/boq/${worksheet.vendor_public_token}`,
                                              token: worksheet.vendor_public_token,
                                              expiresAt: worksheet.validity_date,
                                              type: 'vendor'
                                            });
                                            setPublicLinkDialogOpen(true);
                                          }}>
                                            <Eye size={14} className="mr-2 text-purple-600" />
                                            View Vendor Link
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleRevokeVendorPublicLink(worksheet)}>
                                            <Link2Off size={14} className="mr-2 text-red-600" />
                                            Revoke Vendor Link
                                          </DropdownMenuItem>
                                        </>
                                      ) : (
                                        <DropdownMenuItem onClick={() => handleGenerateVendorPublicLink(worksheet)}>
                                          <Link size={14} className="mr-2 text-purple-600" />
                                          Generate Vendor Link
                                        </DropdownMenuItem>
                                      )}
                                      
                                      <div className="border-t border-stone-100" />
                                      
                                      {/* Extend Validity */}
                                      <DropdownMenuItem onClick={() => {
                                        const newDate = prompt('Enter new validity date (YYYY-MM-DD):', 
                                          new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                                        if (newDate) handleExtendValidity(worksheet, newDate);
                                      }}>
                                        <RefreshCw size={14} className="mr-2 text-stone-600" />
                                        Extend Validity
                                      </DropdownMenuItem>

                                      <DropdownMenuItem onClick={() => openDiffSheet(worksheet)} data-testid={`boq-diff-${worksheet.id}`}>
                                        <Layers size={14} className="mr-2 text-emerald-600" />
                                        View Diff Sheet
                                      </DropdownMenuItem>

                                      <DropdownMenuItem onClick={() => handleUnpublishBOQ(worksheet)} className="text-red-600">
                                        <X size={14} className="mr-2" />
                                        Unpublish
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                ) : (
                                  <Button 
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    onClick={() => openPublishDialog(worksheet)}
                                    title="Publish to client"
                                  >
                                    <Share2 size={14} className="mr-1" />
                                    Publish
                                  </Button>
                                )}
                                
                                {/* Publish to Vendor Button */}
                                {worksheet.vendor_visible ? (
                                  <DropdownMenu modal={true}>
                                    <DropdownMenuTrigger asChild>
                                      <Button 
                                        size="sm"
                                        variant="outline"
                                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                      >
                                        <Users size={14} className="mr-1" />
                                        Vendor
                                        <ChevronDown size={12} className="ml-1" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent side="top" align="end" sideOffset={8} className="w-56 z-[9999]" collisionPadding={20} avoidCollisions={true}>
                                      <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                        Vendor Link
                                      </div>
                                      {worksheet.vendor_public_token ? (
                                        <>
                                          <DropdownMenuItem onClick={() => {
                                            setCurrentPublicLink({
                                              url: `${window.location.origin}/public/boq/${worksheet.vendor_public_token}`,
                                              token: worksheet.vendor_public_token,
                                              expiresAt: worksheet.validity_date,
                                              type: 'vendor'
                                            });
                                            setPublicLinkDialogOpen(true);
                                          }}>
                                            <Eye size={14} className="mr-2 text-purple-600" />
                                            View Vendor Link
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleRevokeVendorPublicLink(worksheet)}>
                                            <Link2Off size={14} className="mr-2 text-red-600" />
                                            Revoke Vendor Link
                                          </DropdownMenuItem>
                                        </>
                                      ) : (
                                        <DropdownMenuItem onClick={() => handleGenerateVendorPublicLink(worksheet)}>
                                          <Link size={14} className="mr-2 text-purple-600" />
                                          Generate Vendor Link
                                        </DropdownMenuItem>
                                      )}
                                      <div className="border-t border-stone-100 mt-1" />
                                      <DropdownMenuItem onClick={() => handleUnpublishFromVendor(worksheet)} className="text-red-600">
                                        <X size={14} className="mr-2" />
                                        Unpublish from Vendors
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                ) : (
                                  <Button 
                                    size="sm"
                                    variant="outline"
                                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    onClick={() => openPublishVendorDialog(worksheet)}
                                    title="Publish to vendors"
                                  >
                                    <Users size={14} className="mr-1" />
                                    Vendor
                                  </Button>
                                )}
                                
                                {/* PHASE 6: Send BOQ via Email (opens new tab) */}
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => window.open(`/boq-worksheets/${worksheet.id}/email?projectId=${worksheet.project_id}`, '_blank')}
                                  title="Send BOQ via Email"
                                  data-testid={`send-boq-email-${worksheet.id}`}
                                >
                                  <Mail size={14} className="mr-1" />
                                  Email
                                </Button>

                                {/* iter-318: Archive / Unarchive BOQ (Owner/Admin/Staff) */}
                                {['owner', 'admin', 'staff'].includes(user.role) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className={worksheet.is_active === false ? 'text-emerald-600 hover:bg-emerald-50' : 'text-stone-500 hover:bg-stone-50'}
                                    onClick={(e) => handleToggleBoqActive(worksheet, e)}
                                    title={worksheet.is_active === false ? 'Restore (mark Active)' : 'Archive (mark Inactive)'}
                                    data-testid={`archive-boq-${worksheet.id}`}
                                  >
                                    {worksheet.is_active === false ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                                  </Button>
                                )}

                                {/* Delete Button - Owner only (Admin excluded) */}
                                {user.role === 'owner' && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="text-red-500 hover:bg-red-50"
                                    onClick={() => { setSelectedWorksheet(worksheet); setDeleteDialogOpen(true); }}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </>
                            )}
                            {isLocked && !isInvoiced && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => navigate('/quotations')}
                                  className="text-purple-600"
                                >
                                  <FileText size={14} className="mr-1" />
                                  View Quotation
                                </Button>
                                {/* Admin Unlock Button - Owner and Admin only */}
                                {['owner', 'admin'].includes(user.role) && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => { setSelectedWorksheet(worksheet); setUnlockDialogOpen(true); }}
                                    className="text-stone-600 hover:bg-stone-50"
                                    title="Unlock BOQ for editing (Owner/Admin only)"
                                    data-testid={`unlock-boq-${worksheet.id}`}
                                  >
                                    <Unlock size={14} className="mr-1" />
                                    Unlock
                                  </Button>
                                )}
                              </>
                            )}
                            {/* P0 FIX: Show read-only badge for vendors */}
                            {user.role === 'vendor' && (
                              <span className="text-xs text-stone-400 italic">View Only</span>
                            )}
                          </div>
                        </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* PHASE 5: Vendor Submissions for this Project */}
                {(() => {
                  const projectSubs = vendorSubmissions.filter(s => s.project_id === projectId);
                  if (projectSubs.length === 0) return null;
                  const vendorColors = ['border-l-purple-500', 'border-l-blue-500', 'border-l-green-500', 'border-l-amber-500', 'border-l-red-500'];
                  const vcMap = {};
                  let ci = 0;
                  return (
                    <div className="px-6 py-4 bg-amber-50/50 border-t border-amber-200" data-testid={`vendor-submissions-${projectId}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <ClipboardCheck size={16} className="text-amber-700" />
                        <h4 className="font-semibold text-amber-800 text-sm">Vendor Quotes Received ({projectSubs.length})</h4>
                      </div>
                      <div className="space-y-2">
                        {projectSubs.map(sub => {
                          if (!vcMap[sub.vendor_name]) { vcMap[sub.vendor_name] = vendorColors[ci % vendorColors.length]; ci++; }
                          return (
                            <div key={sub.id} className={`bg-white rounded-lg p-3 border-l-4 ${vcMap[sub.vendor_name]} flex items-center justify-between text-sm`}>
                              <div>
                                <span className="font-medium text-stone-800">{sub.vendor_name}</span>
                                <span className="text-stone-500 ml-2">{sub.items?.length || 0} items</span>
                                <span className="text-stone-400 ml-2">{formatDate(sub.submitted_at)}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-semibold text-purple-700">₹{(sub.total_amount || 0).toLocaleString()}</span>
                                <Button variant="ghost" size="sm" className="text-blue-600 h-7 text-xs" onClick={() => window.open(`/quotations-received?search=${encodeURIComponent(sub.vendor_name)}`, '_blank')}>
                                  <Eye size={12} className="mr-1" />View
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {/* P1 — Floating Bulk Action Bar */}
        {selectedBoqIds.size > 0 && isAuthorized && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-stone-900 text-white rounded-xl shadow-2xl border border-stone-700 px-5 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4" data-testid="bulk-action-bar">
            <span className="text-sm font-medium">
              <span className="text-amber-300">{selectedBoqIds.size}</span> BOQ{selectedBoqIds.size === 1 ? '' : 's'} selected
            </span>
            <div className="h-5 w-px bg-stone-700" />
            {['owner', 'admin'].includes(user.role) && (
              <>
                <Button size="sm" variant="ghost" className="h-8 text-emerald-300 hover:bg-emerald-500/15 hover:text-emerald-200" onClick={() => runBulkAction('confirm')} disabled={bulkActionRunning} data-testid="bulk-confirm-btn">
                  <CheckCircle2 size={14} className="mr-1.5" />Confirm
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-red-300 hover:bg-red-500/15 hover:text-red-200" onClick={() => runBulkAction('reject')} disabled={bulkActionRunning} data-testid="bulk-reject-btn">
                  <X size={14} className="mr-1.5" />Reject
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-amber-300 hover:bg-amber-500/15 hover:text-amber-200" onClick={() => runBulkAction('pending')} disabled={bulkActionRunning} data-testid="bulk-pending-btn">
                  <Clock size={14} className="mr-1.5" />Pending
                </Button>
                {/* iter-293: Merge multiple BOQs (same project) — only enabled with 2+ selections from a single project */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-indigo-300 hover:bg-indigo-500/15 hover:text-indigo-200 disabled:opacity-40"
                  onClick={openMergeDialog}
                  disabled={bulkActionRunning || !canMerge}
                  data-testid="bulk-merge-btn"
                  title={canMerge ? `Merge ${selectedBoqIds.size} BOQs into one new worksheet` : 'Select 2+ BOQs from the same project to merge'}
                >
                  <GitMerge size={14} className="mr-1.5" />Merge
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-sky-300 hover:bg-sky-500/15 hover:text-sky-200" onClick={() => openTransfer('copy')} disabled={bulkActionRunning} data-testid="bulk-copy-to-project-btn">
                  <Copy size={14} className="mr-1.5" />Copy to Project
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-violet-300 hover:bg-violet-500/15 hover:text-violet-200" onClick={() => openTransfer('move')} disabled={bulkActionRunning} data-testid="bulk-move-to-project-btn">
                  <ArrowRight size={14} className="mr-1.5" />Move to Project
                </Button>
                <div className="h-5 w-px bg-stone-700" />
              </>
            )}
            {['owner', 'staff'].includes(user.role) && (
              <Button size="sm" variant="ghost" className="h-8 text-red-300 hover:bg-red-500/20 hover:text-red-200" onClick={() => setBulkDeleteDialogOpen(true)} disabled={bulkActionRunning} data-testid="bulk-delete-btn">
                <Trash2 size={14} className="mr-1.5" />Delete
              </Button>
            )}
            <div className="h-5 w-px bg-stone-700" />
            <Button size="sm" variant="ghost" className="h-8 text-stone-300 hover:bg-stone-700/50 hover:text-stone-100" onClick={clearSelection} disabled={bulkActionRunning} data-testid="bulk-clear-btn">
              Clear
            </Button>
          </div>
        )}

        {/* P1 — Bulk Delete Confirmation Dialog */}
        <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
          <AlertDialogContent data-testid="bulk-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle size={20} />
                Delete {selectedBoqIds.size} BOQ{selectedBoqIds.size === 1 ? '' : 's'}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the selected BOQ worksheets. BOQs that have already been converted to an invoice will be skipped. Please provide a reason for audit.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <Label className="text-sm">Deletion Reason <span className="text-red-500">*</span></Label>
              <Textarea
                value={bulkDeleteReason}
                onChange={(e) => setBulkDeleteReason(e.target.value)}
                placeholder="e.g. Cleaning up duplicate / test BOQs created during estimation"
                className="mt-1"
                rows={3}
                data-testid="bulk-delete-reason-input"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={bulkActionRunning}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={bulkActionRunning || bulkDeleteReason.trim().length < 3}
                onClick={(e) => { e.preventDefault(); runBulkAction('delete', { reason: bulkDeleteReason.trim() }); }}
                data-testid="bulk-delete-confirm-btn"
              >
                {bulkActionRunning ? 'Deleting…' : `Delete ${selectedBoqIds.size}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <TransferBoqDialog
          open={!!transferMode}
          onOpenChange={(o) => { if (!o) setTransferMode(null); }}
          mode={transferMode || 'copy'}
          ids={Array.from(selectedBoqIds)}
          projects={projects}
          sourceProjectIds={Array.from(mergeProjectIds)}
          onDone={() => { clearSelection(); fetchData(); }}
        />
        {/* iter-293: Merge BOQs Dialog */}
        <AlertDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
          <AlertDialogContent data-testid="bulk-merge-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-indigo-700">
                <GitMerge size={20} />
                Merge {selectedBoqIds.size} BOQ{selectedBoqIds.size === 1 ? '' : 's'} into one
              </AlertDialogTitle>
              <AlertDialogDescription>
                A new draft BOQ worksheet will be created containing every line item from the selected sources. Source BOQs are kept intact (you can delete them later from the bulk bar).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 py-2">
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-stone-700 space-y-1" data-testid="bulk-merge-summary">
                <div><span className="font-semibold">Sources:</span> {selectedBoqsForMerge.map(w => w.worksheet_number || w.name || w.id.slice(0,6)).join(' + ')}</div>
                <div><span className="font-semibold">Project:</span> {selectedBoqsForMerge[0]?.project_name || (Array.from(mergeProjectIds)[0] || '—')}</div>
                <div><span className="font-semibold">Total line items to combine:</span> {selectedBoqsForMerge.reduce((s, w) => s + ((w.items || []).length || 0), 0)}</div>
              </div>
              <div>
                <Label className="text-sm">Merged BOQ name <span className="text-stone-400">(optional)</span></Label>
                <Input
                  value={mergeName}
                  onChange={(e) => setMergeName(e.target.value)}
                  placeholder="Defaults to: Merged BOQ — BOQ-001 + BOQ-002 + …"
                  className="mt-1"
                  data-testid="bulk-merge-name-input"
                />
              </div>
              {/* iter-294: Smart-merge toggle — combines duplicate lines */}
              <label className="flex items-start gap-2 mt-2 cursor-pointer text-sm" data-testid="bulk-merge-smart-toggle-label">
                <input
                  type="checkbox"
                  checked={mergeSmart}
                  onChange={(e) => setMergeSmart(e.target.checked)}
                  className="mt-0.5"
                  data-testid="bulk-merge-smart-toggle"
                />
                <span>
                  <span className="font-medium text-stone-900">Smart-merge: combine identical line items</span>
                  <span className="block text-stone-500 text-xs mt-0.5">
                    Lines sharing the same description, unit, rate, and category will be summed into a single row (recommended).
                  </span>
                </span>
              </label>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={merging}
                onClick={(e) => { e.preventDefault(); performMerge(); }}
                data-testid="bulk-merge-confirm-btn"
              >
                {merging ? 'Merging…' : <><GitMerge size={14} className="mr-1.5" />Merge {selectedBoqIds.size}</>}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <DeleteBOQDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} selectedWorksheet={selectedWorksheet} deleteReason={deleteReason} setDeleteReason={setDeleteReason} onDelete={handleDelete} />

        {/* Edit BOQ Dialog - Full page size (90% of viewport) */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="w-screen max-w-screen h-screen max-h-screen rounded-none border-0 overflow-hidden flex flex-col p-0 gap-0">
            {/* PHASE 7: Full Page BOQ Edit — sticky header */}
            <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-stone-200 shrink-0">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => setEditDialogOpen(false)} className="text-stone-600 hover:bg-stone-100" data-testid="edit-back-btn">
                  <X size={18} className="mr-1" />Back
                </Button>
                <h1 className="text-stone-900 font-manrope text-xl font-bold">Edit BOQ — {selectedWorksheet?.worksheet_number}</h1>
              </div>
              <div className="flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="text-sm">Bulk GST</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={8} className="w-52 z-[9999]" collisionPadding={20} avoidCollisions={true}>
                    {GST_BRACKETS.map(g => (
                      <DropdownMenuItem key={g.value} onClick={() => handleBulkGSTUpdate(parseFloat(g.value))}>
                        Apply {g.label} to all items
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button onClick={handleAddInlineRow} className="apple-button text-sm" data-testid="add-new-row-btn">
                  <Plus size={16} className="mr-2" />Add New Row
                </Button>
                <Button onClick={handleAddHeaderRow} variant="outline" className="text-sm border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="add-header-btn">
                  <LayoutDashboard size={16} className="mr-2" />Add Header
                </Button>
                <Button onClick={handleSaveEdit} disabled={editLoading} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-edit-btn">
                  {editLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Saving...</> : <><Save size={16} className="mr-2" />Save BOQ</>}
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 scrollbar-visible bg-stone-50/50">
              <p className="text-stone-500 text-sm mb-4">Changes sync automatically to linked tasks. Actual values are used for billing. Discount line items will be subtracted from total.</p>
              
              {/* Category Color Legend */}
              {boqCategories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4 p-3 bg-white border border-stone-200 rounded-lg" data-testid="category-color-legend">
                  <span className="text-xs font-semibold text-stone-600 mr-1 self-center">Legend:</span>
                  {boqCategories.filter(c => c.color).map(cat => (
                    <span key={cat.group || cat.code} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border" style={{ borderColor: cat.color, backgroundColor: cat.color + '15' }}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.group_name || cat.group || cat.code}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Items Table - Maximum space for text columns, minimal for numbers */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    <col style={{width: '24px'}} /> {/* Drag handle - minimal */}
                    <col style={{width: '18%'}} /> {/* Item - percentage for flexibility */}
                    <col style={{width: '22%'}} /> {/* Description - more space */}
                    <col style={{width: '15%'}} /> {/* Category */}
                    <col style={{width: '50px'}} /> {/* Est Qty */}
                    <col style={{width: '55px'}} /> {/* Est Unit */}
                    <col style={{width: '70px'}} /> {/* Est Rate */}
                    <col style={{width: '75px'}} /> {/* Est Amt */}
                    <col style={{width: '45px'}} /> {/* Act Qty */}
                    <col style={{width: '70px'}} /> {/* Act Rate */}
                    <col style={{width: '75px'}} /> {/* Act Amt */}
                    <col style={{width: '50px'}} /> {/* GST */}
                    <col style={{width: '50px'}} /> {/* Actions */}
                  </colgroup>
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="p-1" rowSpan={2}></th>
                      <th className="text-left p-1 font-medium" rowSpan={2}>Item</th>
                      <th className="text-left p-1 font-medium" rowSpan={2}>Description</th>
                      <th className="text-center p-1 font-medium bg-purple-50 text-purple-800" rowSpan={2}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-1 justify-center w-full hover:bg-purple-100 rounded px-2 py-1 transition-colors">
                              Category
                              <ChevronDown size={12} className={categorySortBy !== 'none' ? 'text-purple-600' : ''} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="w-48">
                            <DropdownMenuItem 
                              onClick={() => setCategorySortBy('none')}
                              className={categorySortBy === 'none' ? 'bg-purple-50' : ''}
                            >
                              <LayoutDashboard size={14} className="mr-2" />
                              Default Order
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setCategorySortBy('category_asc')}
                              className={categorySortBy === 'category_asc' ? 'bg-purple-50' : ''}
                            >
                              <Tags size={14} className="mr-2" />
                              Category A → Z
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setCategorySortBy('category_desc')}
                              className={categorySortBy === 'category_desc' ? 'bg-purple-50' : ''}
                            >
                              <Tags size={14} className="mr-2 rotate-180" />
                              Category Z → A
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setCategorySortBy('created_asc')}
                              className={categorySortBy === 'created_asc' ? 'bg-purple-50' : ''}
                            >
                              <Clock size={14} className="mr-2" />
                              Oldest First
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setCategorySortBy('created_desc')}
                              className={categorySortBy === 'created_desc' ? 'bg-purple-50' : ''}
                            >
                              <Clock size={14} className="mr-2" />
                              Newest First
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </th>
                      <th className="text-center p-1 font-medium bg-blue-50 text-blue-800" colSpan={4}>Estimated (Design)</th>
                      <th className="text-center p-1 font-medium bg-green-50 text-green-800" colSpan={3}>Actual (Site)</th>
                      <th className="text-center p-1 font-medium" rowSpan={2}>GST%</th>
                      <th rowSpan={2}></th>
                    </tr>
                    <tr>
                      <th className="text-center p-1 font-medium bg-blue-50/50 text-blue-700 text-xs">Qty</th>
                      <th className="text-center p-1 font-medium bg-blue-50/50 text-blue-700 text-xs">Unit</th>
                      <th className="text-right p-1 font-medium bg-blue-50/50 text-blue-700 text-xs">{isCtV(selectedWorksheet) ? 'C.Rate' : 'V.Rate'}</th>
                      <th className="text-right p-1 font-medium bg-blue-50/50 text-blue-700 text-xs">Amt</th>
                      <th className="text-center p-1 font-medium bg-green-50/50 text-green-700 text-xs">Qty</th>
                      <th className="text-right p-1 font-medium bg-green-50/50 text-green-700 text-xs">Rate</th>
                      <th className="text-right p-1 font-medium bg-green-50/50 text-green-700 text-xs">Amt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {/* Sorted items based on categorySortBy */}
                    {(() => {
                      let sortedItems = [...editItems];
                      if (categorySortBy === 'category_asc') {
                        sortedItems.sort((a, b) => {
                          const catA = (a.main_category_code || a.category_group || '').toLowerCase();
                          const catB = (b.main_category_code || b.category_group || '').toLowerCase();
                          return catA.localeCompare(catB);
                        });
                      } else if (categorySortBy === 'category_desc') {
                        sortedItems.sort((a, b) => {
                          const catA = (a.main_category_code || a.category_group || '').toLowerCase();
                          const catB = (b.main_category_code || b.category_group || '').toLowerCase();
                          return catB.localeCompare(catA);
                        });
                      } else if (categorySortBy === 'created_asc') {
                        sortedItems.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
                      } else if (categorySortBy === 'created_desc') {
                        sortedItems.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                      }
                      return sortedItems;
                    })().map((item, displayIndex) => {
                      // Find original index using guaranteed unique _editId
                      const index = editItems.findIndex(ei => ei._editId === item._editId);
                      const actualIndex = index !== -1 ? index : displayIndex;

                      // ── HEADER ROW ──
                      if (item.item_type === 'header') {
                        return (
                          <tr key={item._editId || `header_${displayIndex}`} className="bg-amber-50 border-y-2 border-amber-300" data-testid={`boq-header-row-${displayIndex}`}>
                            <td className="p-0 text-center cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-600"
                                draggable onDragStart={(e) => handleItemDragStart(e, index)} onDragEnd={handleItemDragEnd} onDragOver={(e) => handleItemDragOver(e, index)} onDragLeave={handleItemDragLeave} onDrop={(e) => handleItemDrop(e, index)}>
                              <GripVertical size={12} />
                            </td>
                            <td colSpan={11} className="p-2">
                              <div className="flex items-start gap-2">
                                <LayoutDashboard size={14} className="text-amber-700 flex-shrink-0 mt-1" />
                                {/* iter-312n: auto-growing textarea so long section headers wrap */}
                                <textarea
                                  value={item.task_title || ''}
                                  onChange={(e) => handleItemChange(index, 'task_title', e.target.value)}
                                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                  ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                  rows={1}
                                  className="font-bold text-sm text-amber-900 bg-transparent border-none outline-none flex-1 py-1 resize-none whitespace-pre-wrap break-words leading-snug"
                                  placeholder="Section Header..."
                                  data-testid={`boq-header-input-${displayIndex}`}
                                />
                              </div>
                            </td>
                            <td className="p-1 text-center">
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(index)} className="h-6 w-6 p-0 text-red-500 hover:bg-red-50" data-testid={`delete-header-${displayIndex}`}>
                                <Trash2 size={12} />
                              </Button>
                            </td>
                          </tr>
                        );
                      }

                      const isDiscount = item.is_discount || item.task_title === 'Discount';
                      // Category color for row highlight
                      const rowColor = categoryColorMap[item.main_category_code || item.category_group] || categoryColorMap[item.main_category_id] || '';
                      // MODEL-AWARE: base rate depends on pricing model
                      const itemBaseRate = getBaseRate(item, selectedWorksheet);
                      const estAmount = (item.quantity || 0) * itemBaseRate;
                      // For actual values, default to estimated if not set
                      const actQty = item.actual_quantity ?? item.quantity;
                      const actRate = item.actual_rate ?? item.rate;
                      const actAmount = (actQty || 0) * (actRate || 0);
                      const gstMultiplier = 1 + ((item.gst_percentage || 0) / 100);
                      const isDescExpanded = expandedDescriptions[index];
                      
                      // Get current item's sub-categories based on selected main category
                      const itemMainCat = boqCategories.find(c => 
                        (c.main_category_id || c.id) === item.main_category_id || 
                        c.group === item.main_category_code
                      );
                      const itemSubCategories = itemMainCat?.sub_categories || itemMainCat?.categories || [];
                      const isDragOver = dragOverItemIndex === index;
                      
                      return (
                        <tr 
                          key={item._editId || index} 
                          className={`hover:bg-stone-50 ${isDiscount ? 'bg-red-50' : ''} ${isDragOver ? 'bg-blue-100 border-t-2 border-blue-400' : ''} transition-all`}
                          style={rowColor ? { borderLeft: `6px solid ${rowColor}` } : undefined}
                          draggable
                          onDragStart={(e) => handleItemDragStart(e, index)}
                          onDragEnd={handleItemDragEnd}
                          onDragOver={(e) => handleItemDragOver(e, index)}
                          onDragLeave={handleItemDragLeave}
                          onDrop={(e) => handleItemDrop(e, index)}
                        >
                          {/* Drag Handle - Minimal */}
                          <td className="p-0 text-center cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-600" title="Drag to reorder">
                            <GripVertical size={12} />
                          </td>
                          {/* Item Title - Full width text area */}
                          <td className="p-1">
                            <Textarea
                              value={item.task_title || item.title || ''}
                              onChange={(e) => handleItemChange(index, 'task_title', e.target.value)}
                              className="min-h-[40px] resize-none text-xs border-stone-200 w-full"
                              style={{ height: 'auto', minHeight: '40px' }}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.max(40, e.target.scrollHeight) + 'px';
                              }}
                              data-testid={`item-title-${index}`}
                            />
                          </td>
                          {/* Description - Full width text area */}
                          <td className="p-1">
                            <div className="relative">
                              <Textarea
                                value={item.description || ''}
                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                className={`text-xs border-stone-200 resize-none w-full ${isDescExpanded ? 'min-h-[80px]' : 'min-h-[40px] max-h-[60px] overflow-hidden'}`}
                                style={isDescExpanded ? { height: 'auto' } : {}}
                                placeholder="Description..."
                                onInput={(e) => {
                                  if (isDescExpanded) {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = Math.max(80, e.target.scrollHeight) + 'px';
                                  }
                                }}
                              />
                              {(item.description?.length > 50) && (
                                <button
                                  onClick={() => toggleDescriptionExpand(index)}
                                  className="absolute bottom-1 right-1 text-xs text-blue-600 hover:text-blue-800 bg-white px-1 rounded"
                                >
                                  {isDescExpanded ? <><ChevronUp size={12} className="inline"/> Less</> : <><ChevronDown size={12} className="inline"/> More</>}
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Category Column - Compact dropdowns */}
                          <td className="p-1 bg-purple-50/30">
                            <div className="space-y-1">
                              {/* Main Category */}
                              <SearchableCombobox
                                options={mainCategoryOptions}
                                value={item.main_category_id || item.main_category_code || ''}
                                onValueChange={(catId) => {
                                  const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === catId || c.group === catId);
                                  if (mainCat) {
                                    handleItemBatchChange(index, {
                                      main_category_id: mainCat.main_category_id || mainCat.id,
                                      main_category_code: mainCat.group,
                                      main_category_name: mainCat.group_name,
                                      category_group: mainCat.group,
                                      category_name: mainCat.group_name,
                                      sub_category_id: '',
                                      sub_category_code: '',
                                      sub_category_name: ''
                                    });
                                  }
                                }}
                                placeholder="Main..."
                                searchPlaceholder="Search main..."
                                emptyText="No categories"
                                allowCreate
                                onCreateNew={() => {
                                  setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
                                  setQuickAddCategoryOpen(true);
                                }}
                                createNewText="+ Add Main"
                                className="h-6 text-xs"
                              />
                              {/* Sub Category - Only show if main is selected */}
                              {(item.main_category_id || item.main_category_code) && (
                                <SearchableCombobox
                                  options={itemSubCategories.map(sub => ({
                                    value: sub.id || sub.code,
                                    label: `${sub.code} - ${sub.name}`,
                                    code: sub.code,
                                    name: sub.name,
                                  }))}
                                  value={item.sub_category_id || item.sub_category_code || ''}
                                  onValueChange={(subId) => {
                                    const subCat = itemSubCategories.find(s => (s.id || s.code) === subId);
                                    if (subCat) {
                                      handleItemBatchChange(index, {
                                        sub_category_id: subCat.id || subId,
                                        sub_category_code: subCat.code,
                                        sub_category_name: subCat.name
                                      });
                                    } else {
                                      handleItemBatchChange(index, {
                                        sub_category_id: '',
                                        sub_category_code: '',
                                        sub_category_name: ''
                                      });
                                    }
                                  }}
                                  placeholder="Sub..."
                                  searchPlaceholder="Search sub..."
                                  emptyText="No sub-categories"
                                  allowCreate
                                  onCreateNew={() => {
                                    setNewCategoryData({ 
                                      name: '', 
                                      code: '', 
                                      type: 'sub', 
                                      parentId: item.main_category_id || itemMainCat?.main_category_id || ''
                                    });
                                    setQuickAddCategoryOpen(true);
                                  }}
                                  createNewText="+ Add Sub"
                                  className="h-6 text-xs"
                                />
                              )}
                              {/* Display current selection code */}
                              {item.main_category_code && (
                                <div className="text-[9px] text-purple-600 truncate" title={`${item.main_category_code}${item.sub_category_code ? ' > ' + item.sub_category_code : ''}`}>
                                  {item.main_category_code}{item.sub_category_code ? ` > ${item.sub_category_code}` : ''}
                                </div>
                              )}
                            </div>
                          </td>
                          {/* Estimated - Compact inputs */}
                          <td className="p-0 bg-blue-50/30">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.quantity || 0}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              className="h-7 text-center text-xs w-full px-0.5 border-0 bg-transparent"
                            />
                          </td>
                          <td className="p-0 bg-blue-50/30">
                            <Select 
                              value={item.unit || 'No.s'} 
                              onValueChange={(v) => {
                                handleItemChange(index, 'unit', v);
                                handleItemChange(index, 'actual_unit', v);
                              }}
                            >
                              <SelectTrigger className="h-7 text-xs w-full px-0.5 border-0 bg-transparent"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-0 bg-blue-50/30">
                            <Input
                              type="number"
                              min="0"
                              value={isCtV(selectedWorksheet) ? (item.client_rate ?? item.rate ?? 0) : (item.vendor_rate ?? item.rate ?? 0)}
                              onChange={(e) => handleBaseRateChange(index, parseFloat(e.target.value) || 0)}
                              className="h-7 text-right text-xs w-full px-0.5 border-0 bg-transparent"
                            />
                          </td>
                          <td className="p-0.5 text-right font-semibold text-blue-800 bg-blue-50/30 text-[11px] whitespace-nowrap">
                            ₹{(estAmount * gstMultiplier).toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </td>
                          {/* Actual - Compact inputs */}
                          <td className="p-0 bg-green-50/30">
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              value={item.actual_quantity ?? item.quantity ?? 0}
                              onChange={(e) => handleItemChange(index, 'actual_quantity', parseFloat(e.target.value) || 0)}
                              className="h-7 text-center text-xs w-full px-0.5 border-0 bg-transparent"
                            />
                          </td>
                          <td className="p-0 bg-green-50/30">
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              value={item.actual_rate ?? item.rate ?? 0}
                              onChange={(e) => handleItemChange(index, 'actual_rate', parseFloat(e.target.value) || 0)}
                              className="h-7 text-right text-xs w-full px-0.5 border-0 bg-transparent"
                            />
                          </td>
                          <td className="p-0.5 text-right font-semibold text-green-800 bg-green-50/30 text-[11px] whitespace-nowrap">
                            ₹{(actAmount * gstMultiplier).toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </td>
                          <td className="p-0">
                            <Select value={String(item.gst_percentage || 0)} onValueChange={(v) => handleItemChange(index, 'gst_percentage', parseFloat(v))}>
                              <SelectTrigger className="h-7 text-xs w-full px-0.5 border-0 bg-transparent"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {GST_BRACKETS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-0.5">
                            <div className="flex items-center gap-0.5 justify-center">
                              {item.task_id && (
                                <button 
                                  onClick={() => openInlineTaskEdit(item.task_id)}
                                  className="p-0.5 text-blue-500 hover:bg-blue-50 rounded"
                                  title="Edit Task Details"
                                >
                                  <Pencil size={12} />
                                </button>
                              )}
                              <button onClick={() => handleDeleteItem(index)} className="p-0.5 text-red-500 hover:bg-red-50 rounded" title="Remove from BOQ">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-stone-100 font-medium">
                    {(() => {
                      // Calculate totals (excluding discount and header items)
                      const estBasic = editItems.reduce((sum, item) => {
                        if (item.is_discount || item.item_type === 'header') return sum;
                        const br = getBaseRate(item, selectedWorksheet);
                        return sum + (item.quantity || 0) * br;
                      }, 0);
                      const estGST = editItems.reduce((sum, item) => {
                        if (item.is_discount || item.item_type === 'header') return sum;
                        const br = getBaseRate(item, selectedWorksheet);
                        return sum + (item.quantity || 0) * br * ((item.gst_percentage || 0) / 100);
                      }, 0);
                      const actBasic = editItems.reduce((sum, item) => {
                        if (item.is_discount || item.item_type === 'header') return sum;
                        const actQty = item.actual_quantity ?? item.quantity;
                        const actRate = item.actual_rate ?? item.rate;
                        return sum + (actQty || 0) * (actRate || 0);
                      }, 0);
                      const actGST = editItems.reduce((sum, item) => {
                        if (item.is_discount || item.item_type === 'header') return sum;
                        const actQty = item.actual_quantity ?? item.quantity;
                        const actRate = item.actual_rate ?? item.rate;
                        return sum + (actQty || 0) * (actRate || 0) * ((item.gst_percentage || 0) / 100);
                      }, 0);
                      
                      const estBasicRounded = Math.round(estBasic);
                      const actBasicRounded = Math.round(actBasic);
                      const estGSTRounded = Math.round(estGST);
                      const actGSTRounded = Math.round(actGST);
                      
                      const estDiscountAmt = discountType === 'percentage' 
                        ? Math.round(estBasicRounded * discountValue / 100)
                        : Math.round(discountValue);
                      const actDiscountAmt = discountType === 'percentage' 
                        ? Math.round(actBasicRounded * discountValue / 100)
                        : Math.round(discountValue);
                      const estAfterDiscount = estBasicRounded + estGSTRounded - estDiscountAmt;
                      const actAfterDiscount = actBasicRounded + actGSTRounded - actDiscountAmt;
                      
                      // Overheads
                      const fPct = selectedWorksheet?.freight_pct || 0;
                      const lPct = selectedWorksheet?.labour_handling_pct || 0;
                      const cPct = selectedWorksheet?.consultancy_pct || 0;
                      const estFreight = fPct > 0 ? Math.round(estAfterDiscount * fPct / 100) : 0;
                      const estLabour = lPct > 0 ? Math.round(estAfterDiscount * lPct / 100) : 0;
                      const estConsult = cPct > 0 ? Math.round(estAfterDiscount * cPct / 100) : 0;
                      const actFreight = fPct > 0 ? Math.round(actAfterDiscount * fPct / 100) : 0;
                      const actLabour = lPct > 0 ? Math.round(actAfterDiscount * lPct / 100) : 0;
                      const actConsult = cPct > 0 ? Math.round(actAfterDiscount * cPct / 100) : 0;
                      const estTotal = estAfterDiscount + estFreight + estLabour + estConsult;
                      const actTotal = actAfterDiscount + actFreight + actLabour + actConsult;
                      
                      return (
                        <>
                          {/* Basic Total Row */}
                          <tr>
                            <td colSpan={4} className="p-2 text-right text-stone-600 font-medium">Basic Total:</td>
                            <td colSpan={3} className="p-2 bg-blue-50/50"></td>
                            <td className="p-2 text-right bg-blue-50/50 text-blue-700 font-semibold">₹{estBasicRounded.toLocaleString()}</td>
                            <td colSpan={2} className="p-2 bg-green-50/50"></td>
                            <td className="p-2 text-right bg-green-50/50 text-green-700 font-semibold">₹{actBasicRounded.toLocaleString()}</td>
                            <td colSpan={2}></td>
                          </tr>
                          {/* GST Amount Row */}
                          <tr>
                            <td colSpan={4} className="p-2 text-right text-stone-600 font-medium">GST Amount:</td>
                            <td colSpan={3} className="p-2 bg-blue-50/50"></td>
                            <td className="p-2 text-right bg-blue-50/50 text-blue-600">₹{estGSTRounded.toLocaleString()}</td>
                            <td colSpan={2} className="p-2 bg-green-50/50"></td>
                            <td className="p-2 text-right bg-green-50/50 text-green-600">₹{actGSTRounded.toLocaleString()}</td>
                            <td colSpan={2}></td>
                          </tr>
                          {/* Discount Row */}
                          <tr className="bg-amber-50/30">
                            <td colSpan={4} className="p-2">
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-stone-600 font-medium">Discount:</span>
                                <div className="flex items-center gap-1 bg-white border rounded px-1">
                                  <button
                                    type="button"
                                    onClick={() => setDiscountType('percentage')}
                                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      discountType === 'percentage' 
                                        ? 'bg-stone-200 text-stone-900' 
                                        : 'text-stone-500 hover:text-stone-700'
                                    }`}
                                  >%</button>
                                  <button
                                    type="button"
                                    onClick={() => setDiscountType('value')}
                                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      discountType === 'value' 
                                        ? 'bg-stone-200 text-stone-900' 
                                        : 'text-stone-500 hover:text-stone-700'
                                    }`}
                                  >₹</button>
                                </div>
                                <Input
                                  type="number"
                                  min="0"
                                  step={discountType === 'percentage' ? '0.5' : '100'}
                                  max={discountType === 'percentage' ? '100' : undefined}
                                  value={discountValue || ''}
                                  onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                                  className="w-20 h-7 text-xs text-right"
                                  placeholder="0"
                                />
                              </div>
                            </td>
                            <td colSpan={3} className="p-2 bg-blue-50/30"></td>
                            <td className="p-2 text-right bg-blue-50/30 text-amber-600 font-medium">
                              {estDiscountAmt > 0 ? `-₹${estDiscountAmt.toLocaleString()}` : '-'}
                            </td>
                            <td colSpan={2} className="p-2 bg-green-50/30"></td>
                            <td className="p-2 text-right bg-green-50/30 text-amber-600 font-medium">
                              {actDiscountAmt > 0 ? `-₹${actDiscountAmt.toLocaleString()}` : '-'}
                            </td>
                            <td colSpan={2}></td>
                          </tr>
                          {/* Overhead Rows */}
                          {estFreight > 0 && (
                            <tr>
                              <td colSpan={4} className="p-2 text-right text-stone-600 font-medium">Freight ({fPct}%):</td>
                              <td colSpan={3} className="p-2 bg-blue-50/30"></td>
                              <td className="p-2 text-right bg-blue-50/30 text-stone-700">+₹{estFreight.toLocaleString()}</td>
                              <td colSpan={2} className="p-2 bg-green-50/30"></td>
                              <td className="p-2 text-right bg-green-50/30 text-stone-700">+₹{actFreight.toLocaleString()}</td>
                              <td colSpan={2}></td>
                            </tr>
                          )}
                          {estLabour > 0 && (
                            <tr>
                              <td colSpan={4} className="p-2 text-right text-stone-600 font-medium">Labour Handling ({lPct}%):</td>
                              <td colSpan={3} className="p-2 bg-blue-50/30"></td>
                              <td className="p-2 text-right bg-blue-50/30 text-stone-700">+₹{estLabour.toLocaleString()}</td>
                              <td colSpan={2} className="p-2 bg-green-50/30"></td>
                              <td className="p-2 text-right bg-green-50/30 text-stone-700">+₹{actLabour.toLocaleString()}</td>
                              <td colSpan={2}></td>
                            </tr>
                          )}
                          {estConsult > 0 && (
                            <tr>
                              <td colSpan={4} className="p-2 text-right text-stone-600 font-medium">Consultancy ({cPct}%):</td>
                              <td colSpan={3} className="p-2 bg-blue-50/30"></td>
                              <td className="p-2 text-right bg-blue-50/30 text-stone-700">+₹{estConsult.toLocaleString()}</td>
                              <td colSpan={2} className="p-2 bg-green-50/30"></td>
                              <td className="p-2 text-right bg-green-50/30 text-stone-700">+₹{actConsult.toLocaleString()}</td>
                              <td colSpan={2}></td>
                            </tr>
                          )}
                          {/* Grand Total Row */}
                          <tr className="border-t-2 border-stone-300 bg-stone-200">
                            <td colSpan={4} className="p-2 text-right font-bold text-stone-900">Grand Total:</td>
                            <td colSpan={3} className="p-2 bg-blue-100/70"></td>
                            <td className="p-2 text-right bg-blue-100/70 text-blue-800 font-bold text-base">₹{estTotal.toLocaleString()}</td>
                            <td colSpan={2} className="p-2 bg-green-100/70"></td>
                            <td className="p-2 text-right bg-green-100/70 text-green-800 font-bold text-base">₹{actTotal.toLocaleString()}</td>
                            <td colSpan={2}></td>
                          </tr>
                          {/* Additional Items + Final Total Row */}
                          {additionalItemsSummary && additionalItemsSummary.additional_items_count > 0 && (
                            <>
                              <tr className="bg-amber-50 border-t border-amber-200">
                                <td colSpan={4} className="p-2 text-right font-medium text-amber-800">
                                  Additional Items ({additionalItemsSummary.additional_items_count} items from {additionalItemsSummary.submissions?.length || 0} submissions):
                                </td>
                                <td colSpan={3} className="p-2 bg-amber-100/50"></td>
                                <td className="p-2 text-right bg-amber-100/50 text-amber-800 font-semibold">
                                  +₹{additionalItemsSummary.additional_items_total.toLocaleString()}
                                </td>
                                <td colSpan={4}></td>
                              </tr>
                              <tr className="bg-stone-800 text-white">
                                <td colSpan={4} className="p-2 text-right font-bold text-lg">
                                  FINAL TOTAL:
                                </td>
                                <td colSpan={3} className="p-2"></td>
                                <td className="p-2 text-right font-bold text-lg">
                                  ₹{(estTotal + additionalItemsSummary.additional_items_total).toLocaleString()}
                                </td>
                                <td colSpan={2} className="p-2"></td>
                                <td className="p-2 text-right font-bold text-lg">
                                  ₹{(actTotal + additionalItemsSummary.additional_items_total).toLocaleString()}
                                </td>
                                <td colSpan={2} className="p-2">
                                  <button
                                    onClick={handleSaveFinalTotal}
                                    disabled={savingFinalTotal}
                                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded flex items-center gap-1 disabled:opacity-50"
                                    data-testid="save-final-total-btn"
                                  >
                                    <Save size={12} />
                                    {savingFinalTotal ? 'Saving...' : 'Save'}
                                  </button>
                                </td>
                              </tr>
                              {additionalItemsSummary.saved_final_total && (
                                <tr className="bg-green-50 border-t border-green-200">
                                  <td colSpan={12} className="p-2 text-center text-xs text-green-700">
                                    <CheckCircle2 size={12} className="inline mr-1" />
                                    Saved Final Total: ₹{additionalItemsSummary.saved_final_total.toLocaleString()}
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Line Item Dialog */}
        <DeleteItemDialog open={deleteItemDialogOpen} onOpenChange={setDeleteItemDialogOpen} itemToDelete={itemToDelete} deleteAction={deleteAction} setDeleteAction={setDeleteAction} onConfirm={confirmDeleteItem} />

        {/* Add Line Item Dialog */}
        <Dialog open={addItemDialogOpen} onOpenChange={(open) => {
          setAddItemDialogOpen(open);
          if (open) fetchBoqCategories(); // Load categories when dialog opens
        }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl flex items-center gap-2">
                <Plus size={20} />
                Add Line Item
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              <p className="text-sm text-stone-500">This will create a new approved task and add it to the BOQ.</p>
              
              {/* Category Selection - NEW */}
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-stone-700">
                  <Tags size={16} />
                  Category Selection *
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-stone-600">Main Category *</Label>
                    <SearchableCombobox
                      options={mainCategoryOptions}
                      value={newItemData.main_category_id}
                      onValueChange={handleMainCategorySelect}
                      placeholder={categoriesLoading ? "Loading..." : "Select main category"}
                      searchPlaceholder="Search categories..."
                      emptyText="No categories found"
                      allowCreate
                      onCreateNew={() => {
                        setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
                        setQuickAddCategoryOpen(true);
                      }}
                      createNewText="+ Add Main Category"
                      disabled={categoriesLoading}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600">Sub Category</Label>
                    <SearchableCombobox
                      options={getSubCategoryOptions(newItemData.main_category_id)}
                      value={newItemData.sub_category_id}
                      onValueChange={handleSubCategorySelect}
                      placeholder={newItemData.main_category_id ? "Select sub-category" : "Select main first"}
                      searchPlaceholder="Search sub-categories..."
                      emptyText="No sub-categories"
                      allowCreate={!!newItemData.main_category_id}
                      onCreateNew={() => {
                        setNewCategoryData({ name: '', code: '', type: 'sub', parentId: newItemData.main_category_id });
                        setQuickAddCategoryOpen(true);
                      }}
                      createNewText="+ Add Sub-Category"
                      disabled={!newItemData.main_category_id}
                      className="mt-1"
                    />
                  </div>
                </div>
                {newItemData.main_category_code && (
                  <div className="text-xs text-stone-500">
                    Selected: <span className="font-medium">{newItemData.main_category_code} - {newItemData.main_category_name}</span>
                    {newItemData.sub_category_code && <span> / {newItemData.sub_category_code} - {newItemData.sub_category_name}</span>}
                  </div>
                )}
              </div>

              <div>
                <Label>Title *</Label>
                <Input 
                  value={newItemData.title} 
                  onChange={(e) => setNewItemData({ ...newItemData, title: e.target.value })} 
                  placeholder="e.g., Kitchen Cabinet" 
                  className="mt-1"
                  data-testid="add-item-title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newItemData.description} onChange={(e) => setNewItemData({ ...newItemData, description: e.target.value })} placeholder="Optional..." className="mt-1" rows={2} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input type="number" min="0" step="0.01" value={newItemData.quantity} onChange={(e) => setNewItemData({ ...newItemData, quantity: parseFloat(e.target.value) || 0 })} className="mt-1" />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={newItemData.unit} onValueChange={(v) => setNewItemData({ ...newItemData, unit: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{isCtV(selectedWorksheet) ? 'Client Rate (₹)' : 'Vendor Rate (₹)'}</Label>
                  <Input type="number" min="0" value={newItemData.rate} onChange={(e) => setNewItemData({ ...newItemData, rate: parseFloat(e.target.value) || 0 })} className="mt-1" />
                </div>
              </div>
              <div>
                <Label>GST Rate</Label>
                <Select value={String(newItemData.gst_percentage)} onValueChange={(v) => setNewItemData({ ...newItemData, gst_percentage: parseFloat(v) })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{GST_BRACKETS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {newItemData.quantity > 0 && newItemData.rate > 0 && (
                <div className="p-3 bg-stone-50 rounded-lg text-sm">
                  <div className="flex justify-between"><span>Amount:</span><span>₹{(newItemData.quantity * newItemData.rate).toLocaleString()}</span></div>
                  {newItemData.gst_percentage > 0 && (
                    <>
                      <div className="flex justify-between"><span>GST ({newItemData.gst_percentage}%):</span><span>₹{((newItemData.quantity * newItemData.rate * newItemData.gst_percentage) / 100).toLocaleString()}</span></div>
                      <div className="flex justify-between font-bold border-t pt-2 mt-2"><span>Total:</span><span>₹{((newItemData.quantity * newItemData.rate) * (1 + newItemData.gst_percentage / 100)).toLocaleString()}</span></div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setAddItemDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddLineItem} className="apple-button" data-testid="add-item-submit"><Plus size={16} className="mr-2" />Add to BOQ</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Add Category Dialog */}
        <Dialog open={quickAddCategoryOpen} onOpenChange={setQuickAddCategoryOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-lg flex items-center gap-2">
                <Tags size={18} />
                {newCategoryData.type === 'main' ? 'Add Main Category' : 'Add Sub-Category'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {newCategoryData.type === 'main' ? (
                <>
                  <div>
                    <Label>Category Code *</Label>
                    <Input 
                      value={newCategoryData.code} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, code: e.target.value.toUpperCase() })} 
                      placeholder="e.g., A, B, C..."
                      maxLength={3}
                      className="mt-1"
                    />
                    <p className="text-xs text-stone-500 mt-1">Single letter or short code</p>
                  </div>
                  <div>
                    <Label>Category Name *</Label>
                    <Input 
                      value={newCategoryData.name} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })} 
                      placeholder="e.g., Civil Works, Electrical..."
                      className="mt-1"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>Sub-Category Name *</Label>
                    <Input 
                      value={newCategoryData.name} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })} 
                      placeholder="e.g., Wiring, Switches..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Code (optional)</Label>
                    <Input 
                      value={newCategoryData.code} 
                      onChange={(e) => setNewCategoryData({ ...newCategoryData, code: e.target.value })} 
                      placeholder="Auto-generated if empty"
                      className="mt-1"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setQuickAddCategoryOpen(false)}>Cancel</Button>
              <Button onClick={handleQuickAddCategory} className="apple-button">
                <Plus size={16} className="mr-2" />Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Unlock BOQ Dialog (Owner only) */}
        <UnlockBOQDialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen} selectedWorksheet={selectedWorksheet} unlockReason={unlockReason} setUnlockReason={setUnlockReason} onUnlock={handleUnlock} onCancel={() => { setUnlockReason(''); setSelectedWorksheet(null); }} />
        
        {/* Publish to Client Dialog with Validity Date */}
        <PublishClientDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen} publishWorksheet={publishWorksheet} validityDate={validityDate} setValidityDate={setValidityDate} generatePublicLink={generatePublicLink} setGeneratePublicLink={setGeneratePublicLink} publishing={publishing} onPublish={handlePublishToClient} />
        
        {/* Publish to Vendor Dialog */}
        <PublishVendorDialog open={publishVendorDialogOpen} onOpenChange={setPublishVendorDialogOpen} publishVendorWorksheet={publishVendorWorksheet} generateVendorPublicLink={generateVendorPublicLink} setGenerateVendorPublicLink={setGenerateVendorPublicLink} publishingVendor={publishingVendor} onPublish={handlePublishToVendor} />
        
        {/* Public Link Dialog */}
        <PublicLinkDialog open={publicLinkDialogOpen} onOpenChange={setPublicLinkDialogOpen} currentPublicLink={currentPublicLink} onCopyLink={copyToClipboard} />

        {/* === DIFF SHEET MODAL — "What changed since your last approved version" === */}
        <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
          <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-stone-800">
                <Layers size={18} className="text-emerald-600" />
                Diff Sheet — <span className="font-normal text-stone-600 text-sm">{diffWorksheet?.name}</span>
              </DialogTitle>
            </DialogHeader>
            {diffLoading && <div className="py-16 text-center text-stone-500 text-sm">Loading comparison…</div>}
            {!diffLoading && diffData && !diffData.has_previous_snapshot && (
              <div className="py-12 text-center">
                <Layers size={40} className="mx-auto text-stone-300 mb-3" />
                <p className="text-sm font-semibold text-stone-700">No previously published snapshot</p>
                <p className="text-xs text-stone-500 mt-1">This BOQ was just published — the Diff Sheet will compare future publishes against this one.</p>
              </div>
            )}
            {!diffLoading && diffData && diffData.has_previous_snapshot && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="rounded-lg bg-stone-50 p-3 border border-stone-200">
                    <p className="text-stone-500 uppercase tracking-wider">Previous (Snapshot)</p>
                    <p className="text-sm font-semibold text-stone-800 mt-1">
                      ₹{Number(diffData.previous_subtotal).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-[11px] text-stone-500 mt-0.5">
                      {diffData.previous_snapshot_at ? new Date(diffData.previous_snapshot_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <div className="rounded-lg bg-stone-50 p-3 border border-stone-200">
                    <p className="text-stone-500 uppercase tracking-wider">Current</p>
                    <p className="text-sm font-semibold text-stone-800 mt-1">
                      ₹{Number(diffData.current_subtotal).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-[11px] text-stone-500 mt-0.5">
                      {diffData.current_snapshot_at ? new Date(diffData.current_snapshot_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <div className={`rounded-lg p-3 border ${Number(diffData.subtotal_delta) > 0 ? 'bg-red-50 border-red-200' : Number(diffData.subtotal_delta) < 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-200'}`}>
                    <p className="uppercase tracking-wider text-stone-500">Net change</p>
                    <p className={`text-sm font-bold mt-1 ${Number(diffData.subtotal_delta) > 0 ? 'text-red-700' : Number(diffData.subtotal_delta) < 0 ? 'text-emerald-700' : 'text-stone-700'}`}>
                      {Number(diffData.subtotal_delta) > 0 ? '+' : ''}₹{Number(diffData.subtotal_delta).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      <span className="text-[11px] font-normal ml-1">({diffData.subtotal_pct > 0 ? '+' : ''}{diffData.subtotal_pct}%)</span>
                    </p>
                    <p className="text-[11px] text-stone-500 mt-0.5">{diffData.unchanged_count} unchanged · {diffData.added.length} added · {diffData.removed.length} removed · {diffData.changed.length} changed</p>
                  </div>
                </div>

                {/* Added lines (green) */}
                {diffData.added.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">+ Added ({diffData.added.length})</p>
                    <table className="w-full text-xs border border-emerald-200 rounded-md overflow-hidden">
                      <thead className="bg-emerald-50"><tr><th className="text-left px-3 py-1.5">Item</th><th className="text-right px-3 py-1.5 w-24">Qty</th><th className="text-right px-3 py-1.5 w-28">Rate</th><th className="text-right px-3 py-1.5 w-32">Amount</th></tr></thead>
                      <tbody>{diffData.added.map((i, idx) => (
                        <tr key={idx} className="border-t border-emerald-100 bg-emerald-50/30"><td className="px-3 py-1.5">{i.task_title}</td><td className="px-3 py-1.5 text-right">{i.quantity} {i.unit}</td><td className="px-3 py-1.5 text-right">₹{Number(i.rate).toLocaleString('en-IN')}</td><td className="px-3 py-1.5 text-right font-semibold">₹{Number(i.amount).toLocaleString('en-IN')}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}

                {/* Removed lines (red) */}
                {diffData.removed.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wider mb-1">− Removed ({diffData.removed.length})</p>
                    <table className="w-full text-xs border border-red-200 rounded-md overflow-hidden">
                      <thead className="bg-red-50"><tr><th className="text-left px-3 py-1.5">Item</th><th className="text-right px-3 py-1.5 w-24">Qty</th><th className="text-right px-3 py-1.5 w-28">Rate</th><th className="text-right px-3 py-1.5 w-32">Amount</th></tr></thead>
                      <tbody>{diffData.removed.map((i, idx) => (
                        <tr key={idx} className="border-t border-red-100 bg-red-50/30 line-through text-stone-500"><td className="px-3 py-1.5">{i.task_title}</td><td className="px-3 py-1.5 text-right">{i.quantity} {i.unit}</td><td className="px-3 py-1.5 text-right">₹{Number(i.rate).toLocaleString('en-IN')}</td><td className="px-3 py-1.5 text-right font-semibold">₹{Number(i.amount).toLocaleString('en-IN')}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}

                {/* Changed lines (amber) */}
                {diffData.changed.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">~ Changed ({diffData.changed.length})</p>
                    <table className="w-full text-xs border border-amber-200 rounded-md overflow-hidden">
                      <thead className="bg-amber-50"><tr><th className="text-left px-3 py-1.5">Item</th><th className="text-right px-3 py-1.5 w-28">Prev Rate</th><th className="text-right px-3 py-1.5 w-28">Curr Rate</th><th className="text-right px-3 py-1.5 w-24">Δ%</th><th className="text-right px-3 py-1.5 w-32">Prev → Curr</th></tr></thead>
                      <tbody>{diffData.changed.map((i, idx) => {
                        const up = i.rate_delta > 0;
                        return (<tr key={idx} className="border-t border-amber-100">
                          <td className="px-3 py-1.5">{i.task_title}<span className="text-[10px] text-stone-400 ml-1">({i.prev_quantity} → {i.curr_quantity} {i.unit})</span></td>
                          <td className="px-3 py-1.5 text-right text-stone-500">₹{Number(i.prev_rate).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-1.5 text-right font-semibold">₹{Number(i.curr_rate).toLocaleString('en-IN')}</td>
                          <td className={`px-3 py-1.5 text-right font-bold ${up ? 'text-red-600' : 'text-emerald-600'}`}>{up ? '+' : ''}{i.rate_pct}%</td>
                          <td className="px-3 py-1.5 text-right">₹{Number(i.prev_amount).toLocaleString('en-IN')} → <b>₹{Number(i.curr_amount).toLocaleString('en-IN')}</b></td>
                        </tr>);
                      })}</tbody>
                    </table>
                  </div>
                )}

                {diffData.added.length === 0 && diffData.removed.length === 0 && diffData.changed.length === 0 && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-center">
                    <p className="text-sm font-semibold text-emerald-800">No changes since the last publish.</p>
                    <p className="text-xs text-emerald-700 mt-1">This BOQ is identical to your previously approved version.</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>


        {/* BOQ Summary Dialog */}
        <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LayoutDashboard className="text-stone-700" size={20} />
                BOQ Summary Dashboard
                {summaryData?.commercial_snapshot?.value_mode === 'actual' && (
                  <span
                    className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-300 ml-2"
                    data-testid="boq-summary-actual-badge"
                    title="Figures shown reflect Actual (Site) quantities & rates"
                  >
                    Actual (Site)
                  </span>
                )}
                {summaryData?.commercial_snapshot?.value_mode === 'estimated' && (
                  <span
                    className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300 ml-2"
                    data-testid="boq-summary-estimated-badge"
                    title="Figures shown reflect Estimated (Design) quantities & rates"
                  >
                    Estimated (Design)
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {summaryData && (
              <div className="space-y-6 py-4">
                {/* View Role Toggle */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-stone-600">Preview as:</span>
                  <Tabs value={summaryViewRole} onValueChange={(value) => {
                    setSummaryViewRole(value);
                    if (summaryData?.worksheet_id) {
                      fetchSummary(summaryData.worksheet_id, value);
                    }
                  }}>
                    <TabsList className="h-8">
                      <TabsTrigger value="vendor" className="text-xs px-3 h-6">Vendor</TabsTrigger>
                      <TabsTrigger value="admin" className="text-xs px-3 h-6">Admin</TabsTrigger>
                      <TabsTrigger value="client" className="text-xs px-3 h-6">Client</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                {/* Header Section */}
                <div className="bg-[#1A1A1A] text-white p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold">{summaryData.header?.boq_title || 'BOQ Summary'}</h2>
                      <p className="text-stone-300 text-sm">{summaryData.header?.project_name}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-stone-300">BOQ #: {summaryData.header?.boq_number}</p>
                      <p className="text-stone-400">Valid Until: {summaryData.header?.valid_until || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Project Overview */}
                <div className="rounded-lg border border-stone-200 overflow-hidden">
                  <div className="bg-stone-100 px-4 py-2 font-semibold text-sm text-stone-700">
                    Project Overview
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-stone-100">
                    <div className="divide-y divide-stone-100">
                      <div className="flex text-sm">
                        <div className="w-1/2 px-4 py-2 bg-stone-50 text-stone-600">Client Name</div>
                        <div className="w-1/2 px-4 py-2">{summaryData.project_overview?.client_name}</div>
                      </div>
                      <div className="flex text-sm">
                        <div className="w-1/2 px-4 py-2 bg-stone-50 text-stone-600">Project Name</div>
                        <div className="w-1/2 px-4 py-2">{summaryData.project_overview?.project_name}</div>
                      </div>
                      <div className="flex text-sm">
                        <div className="w-1/2 px-4 py-2 bg-stone-50 text-stone-600">Location</div>
                        <div className="w-1/2 px-4 py-2">{summaryData.project_overview?.site_location || '-'}</div>
                      </div>
                    </div>
                    <div className="divide-y divide-stone-100">
                      <div className="flex text-sm">
                        <div className="w-1/2 px-4 py-2 bg-stone-50 text-stone-600">Project Type</div>
                        <div className="w-1/2 px-4 py-2">{summaryData.project_overview?.project_type}</div>
                      </div>
                      <div className="flex text-sm">
                        <div className="w-1/2 px-4 py-2 bg-stone-50 text-stone-600">Service Model</div>
                        <div className="w-1/2 px-4 py-2">{summaryData.project_overview?.service_model}</div>
                      </div>
                      <div className="flex text-sm">
                        <div className="w-1/2 px-4 py-2 bg-stone-50 text-stone-600">Prepared By</div>
                        <div className="w-1/2 px-4 py-2">{summaryData.header?.prepared_by}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Commercial Snapshot */}
                {summaryData.commercial_snapshot && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border border-stone-200 overflow-hidden">
                      <div className="bg-stone-100 px-4 py-2 font-semibold text-sm text-stone-700">
                        Commercial Snapshot
                      </div>
                      <div className="divide-y divide-stone-100">
                        <div className="flex justify-between px-4 py-2 text-sm">
                          <span className="text-stone-600">Total BOQ Value</span>
                          <span className="font-semibold">₹{summaryData.commercial_snapshot.total_boq_value?.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between px-4 py-2 text-sm bg-stone-50">
                          <span className="text-stone-600">GST ({summaryData.commercial_snapshot.gst_percentage}%)</span>
                          <span className="font-semibold">₹{summaryData.commercial_snapshot.gst_amount?.toLocaleString('en-IN')}</span>
                        </div>
                        {summaryData.commercial_snapshot.discount_amount > 0 && (
                          <div className="flex justify-between px-4 py-2 text-sm bg-green-50">
                            <span className="text-green-700">Discount</span>
                            <span className="font-semibold text-green-700">-₹{summaryData.commercial_snapshot.discount_amount?.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        {summaryData.commercial_snapshot.overhead_freight > 0 && (
                          <div className="flex justify-between px-4 py-2 text-sm">
                            <span className="text-stone-600">Freight ({summaryData.commercial_snapshot.freight_pct}%)</span>
                            <span className="font-semibold">+₹{summaryData.commercial_snapshot.overhead_freight?.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        {summaryData.commercial_snapshot.overhead_labour > 0 && (
                          <div className="flex justify-between px-4 py-2 text-sm bg-stone-50">
                            <span className="text-stone-600">Labour ({summaryData.commercial_snapshot.labour_handling_pct}%)</span>
                            <span className="font-semibold">+₹{summaryData.commercial_snapshot.overhead_labour?.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        {summaryData.commercial_snapshot.overhead_consultancy > 0 && (
                          <div className="flex justify-between px-4 py-2 text-sm">
                            <span className="text-stone-600">Consultancy ({summaryData.commercial_snapshot.consultancy_pct}%)</span>
                            <span className="font-semibold">+₹{summaryData.commercial_snapshot.overhead_consultancy?.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        <div className="flex justify-between px-4 py-3 bg-gradient-to-r from-stone-700 to-stone-600 text-white">
                          <span className="font-semibold">Grand Total</span>
                          <span className="font-bold text-lg">₹{summaryData.commercial_snapshot.grand_total?.toLocaleString('en-IN')}</span>
                        </div>
                        {summaryData.commercial_snapshot.additional_items_total > 0 && (
                          <>
                            <div className="flex justify-between px-4 py-2 text-sm bg-amber-50">
                              <span className="text-amber-800 font-medium">Additional Items</span>
                              <span className="font-semibold text-amber-800">+₹{summaryData.commercial_snapshot.additional_items_total?.toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between px-4 py-3 bg-gradient-to-r from-green-700 to-green-600 text-white">
                              <span className="font-semibold">Final Total</span>
                              <span className="font-bold text-lg">₹{summaryData.commercial_snapshot.final_total?.toLocaleString('en-IN')}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-lg p-4 text-white">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-stone-300 text-sm">Categories</span>
                          <span className="font-bold text-xl">{summaryData.commercial_snapshot.total_categories}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-300 text-sm">Line Items</span>
                          <span className="font-bold text-xl">{summaryData.commercial_snapshot.total_line_items}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-300 text-sm">Estimated Duration</span>
                          <span className="font-bold text-xl">{summaryData.commercial_snapshot.estimated_duration_days || '-'} Days</span>
                        </div>
                        {['owner', 'admin', 'staff'].includes(user.role) && (
                          <div className="pt-3 border-t border-stone-700 space-y-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                setSavingFinalTotal(true);
                                try {
                                  const response = await axios.post(`${API}/boq-worksheets/${summaryData.worksheet_id}/save-final-total`, {}, {
                                    headers: { Authorization: `Bearer ${user.token || localStorage.getItem('token')}` }
                                  });
                                  toast.success(response.data.message);
                                  fetchSummary(summaryData.worksheet_id, summaryViewRole);
                                  fetchData();
                                  if (showHistory) fetchFinalTotalHistory(summaryData.worksheet_id);
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to save Final Total');
                                } finally {
                                  setSavingFinalTotal(false);
                                }
                              }}
                              disabled={savingFinalTotal}
                              className="w-full bg-green-600 hover:bg-green-700 text-white text-xs"
                              data-testid="summary-save-final-total-btn"
                            >
                              <Save size={12} className="mr-1" />
                              {savingFinalTotal ? 'Saving...' : 'Save Final Total'}
                            </Button>
                            <button
                              onClick={() => {
                                const next = !showHistory;
                                setShowHistory(next);
                                if (next) fetchFinalTotalHistory(summaryData.worksheet_id);
                              }}
                              className="w-full text-center text-[10px] text-stone-400 hover:text-stone-200 underline"
                              data-testid="toggle-history-btn"
                            >
                              {showHistory ? 'Hide History' : 'View Cost History'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Final Total History Timeline */}
                    {showHistory && finalTotalHistory.length > 0 && (
                      <div className="mt-3 rounded-lg border border-stone-200 overflow-hidden" data-testid="final-total-history">
                        <div className="bg-stone-100 px-4 py-2 font-semibold text-sm text-stone-700 flex items-center justify-between">
                          <span>Cost Evolution</span>
                          <span className="text-xs font-normal text-stone-500">{finalTotalHistory.length} snapshots</span>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto divide-y divide-stone-100">
                          {finalTotalHistory.map((entry, idx) => {
                            // Select the correct grand total based on active view tab
                            const viewKey = summaryViewRole === 'client' ? 'client_grand_total' : summaryViewRole === 'vendor' ? 'vendor_grand_total' : 'admin_grand_total';
                            const displayTotal = entry[viewKey] ?? entry.grand_total;
                            const displayFinal = displayTotal + (entry.additional_items_total || 0);
                            const prev = finalTotalHistory[idx + 1];
                            const prevTotal = prev ? (prev[viewKey] ?? prev.grand_total) + (prev.additional_items_total || 0) : 0;
                            const diff = prev ? displayFinal - prevTotal : 0;
                            const diffPct = prev && prevTotal ? ((diff / prevTotal) * 100).toFixed(1) : null;
                            return (
                              <div key={entry.id} className={`px-4 py-2 text-xs ${idx === 0 ? 'bg-green-50' : ''}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`font-bold ${idx === 0 ? 'text-green-700' : 'text-stone-700'}`}>
                                    ₹{displayFinal?.toLocaleString('en-IN')}
                                  </span>
                                  {diff !== 0 && (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${diff > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                      {diff > 0 ? '+' : ''}₹{diff.toLocaleString('en-IN')}
                                      {diffPct && ` (${diff > 0 ? '+' : ''}${diffPct}%)`}
                                    </span>
                                  )}
                                  {idx === 0 && (
                                    <span className="px-1.5 py-0.5 bg-green-600 text-white rounded text-[10px] font-medium">Current</span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between text-stone-500">
                                  <span>Grand: ₹{displayTotal?.toLocaleString('en-IN')} + Addl: ₹{(entry.additional_items_total || 0)?.toLocaleString('en-IN')}</span>
                                  <span>{formatDate(entry.saved_at)}</span>
                                </div>
                                <div className="text-stone-400 text-[10px] mt-0.5">by {entry.saved_by_name}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {showHistory && finalTotalHistory.length === 0 && (
                      <div className="mt-3 rounded-lg border border-stone-200 p-4 text-center text-xs text-stone-400">
                        No history yet. Save the Final Total to start tracking cost evolution.
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Structure & Scope Highlights */}
                {summaryData.payment_structure && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border border-stone-200 overflow-hidden">
                      <div className="bg-stone-100 px-4 py-2 font-semibold text-sm text-stone-700">
                        Payment Schedule
                      </div>
                      <div className="divide-y divide-stone-100 text-sm">
                        {Object.entries(summaryData.payment_structure).map(([key, value], idx) => (
                          <div key={key} className={`flex justify-between px-4 py-2 ${idx % 2 === 1 ? 'bg-stone-50' : ''}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-stone-700 font-bold">{value.percentage}%</span>
                              <span className="text-stone-600">{value.milestone_name || key.replace('_', ' ')}</span>
                              {value.description && (
                                <span className="text-stone-400 text-xs">({value.description})</span>
                              )}
                            </div>
                            <span className="font-semibold">Rs.{value.amount?.toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {summaryData.scope_highlights && summaryData.scope_highlights.length > 0 && (
                      <div className="rounded-lg border border-stone-200 overflow-hidden">
                        <div className="bg-stone-100 px-4 py-2 font-semibold text-sm text-stone-700">
                          Scope Highlights
                        </div>
                        <div className="p-4">
                          <ul className="space-y-2">
                            {summaryData.scope_highlights.map((cat, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm">
                                <CheckCircle size={14} className="text-stone-600" />
                                <span className="text-stone-700">{cat}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Important Notes */}
                {summaryData.important_notes && (
                  <div className="rounded-lg border border-stone-200 overflow-hidden">
                    <div className="bg-[#1A1A1A] px-4 py-2 font-semibold text-sm text-white">
                      Important Notes
                    </div>
                    <div className="p-4">
                      <ul className="space-y-2">
                        {summaryData.important_notes.map((note, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle size={14} className="text-stone-600 mt-0.5 flex-shrink-0" />
                            <span className="text-stone-600">{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  const token = localStorage.getItem('token');
                  window.open(`${API}/boq-worksheets/${summaryData?.worksheet_id}/export/summary-pdf?export_role=${summaryViewRole}`, '_blank');
                }}
                className="bg-stone-900 text-white hover:bg-stone-800"
                data-testid="download-summary-pdf"
              >
                <Download size={14} className="mr-2" />
                Download Summary PDF
              </Button>
              <Button variant="outline" onClick={() => setSummaryDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Inline Task Edit Dialog - Full Edit Task dialog matching Tasks.js */}
        <Dialog open={inlineTaskEditOpen} onOpenChange={setInlineTaskEditOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-visible">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-2xl">Edit Task</DialogTitle>
            </DialogHeader>
            
            {inlineTaskLoading ? (
              <div className="py-8 text-center text-stone-500">Loading task...</div>
            ) : inlineTaskData ? (
              <div className="space-y-4 py-2">
                {/* Task Title */}
                <div>
                  <Label className="text-stone-900">Task Title</Label>
                  <Input 
                    value={inlineTaskData.title || ''}
                    onChange={(e) => setInlineTaskData({...inlineTaskData, title: e.target.value})}
                    className="bg-white text-stone-900 border-stone-300"
                    placeholder="Task title..."
                  />
                </div>
                
                {/* Description with AI Generate */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-stone-900">Description</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={generateInlineAIDescription}
                      disabled={inlineAiDescLoading || !inlineTaskData.title}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 px-2 text-xs"
                    >
                      {inlineAiDescLoading ? (
                        <span className="flex items-center gap-1"><span className="animate-spin">⏳</span> Generating...</span>
                      ) : (
                        <span className="flex items-center gap-1"><Wand2 size={14} /> AI Generate</span>
                      )}
                    </Button>
                  </div>
                  <Textarea 
                    value={inlineTaskData.description || ''}
                    onChange={(e) => setInlineTaskData({...inlineTaskData, description: e.target.value})}
                    className="bg-white text-stone-900 border-stone-300 min-h-[80px] resize-y"
                    placeholder="Task description..."
                    rows={3}
                  />
                </div>
                
                {/* Category Selection with +Add buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-stone-900">Main Category</Label>
                      <button
                        type="button"
                        onClick={() => {
                          setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
                          setQuickAddCategoryOpen(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus size={12} /> Add
                      </button>
                    </div>
                    <SearchableCombobox
                      options={boqCategories.map(cat => ({
                        value: cat.group || cat.code,
                        label: `${cat.group || cat.code} - ${cat.group_name || cat.name}`,
                      }))}
                      value={inlineTaskData.category_group || ''}
                      onValueChange={(value) => setInlineTaskData({ 
                        ...inlineTaskData, 
                        category_group: value,
                        category_code: '',
                      })}
                      placeholder="Search main category..."
                      searchPlaceholder="Type to search..."
                      emptyText="No categories found"
                      allowCreate
                      onCreateNew={() => {
                        setNewCategoryData({ name: '', code: '', type: 'main', parentId: '' });
                        setQuickAddCategoryOpen(true);
                      }}
                      createNewText="+ Add Main Category"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-stone-900">Sub-Category</Label>
                      {inlineTaskData.category_group && (
                        <button
                          type="button"
                          onClick={() => {
                            const mainCat = boqCategories.find(c => c.group === inlineTaskData.category_group || c.code === inlineTaskData.category_group);
                            setNewCategoryData({ 
                              name: '', 
                              code: '', 
                              type: 'sub', 
                              parentId: mainCat?.main_category_id || mainCat?.id || ''
                            });
                            setQuickAddCategoryOpen(true);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Plus size={12} /> Add
                        </button>
                      )}
                    </div>
                    <SearchableCombobox
                      options={(() => {
                        const mainCat = boqCategories.find(c => c.group === inlineTaskData.category_group || c.code === inlineTaskData.category_group);
                        return (mainCat?.categories || mainCat?.sub_categories || []).map(cat => ({
                          value: cat.code,
                          label: `${cat.code} - ${cat.name}`,
                        }));
                      })()}
                      value={inlineTaskData.category_code || ''}
                      onValueChange={(value) => setInlineTaskData({ ...inlineTaskData, category_code: value })}
                      placeholder={inlineTaskData.category_group ? "Search sub-category..." : "Select main first"}
                      searchPlaceholder="Type to search..."
                      emptyText="No sub-categories"
                      disabled={!inlineTaskData.category_group}
                      allowCreate={!!inlineTaskData.category_group}
                      onCreateNew={() => {
                        const mainCat = boqCategories.find(c => c.group === inlineTaskData.category_group || c.code === inlineTaskData.category_group);
                        setNewCategoryData({ 
                          name: '', 
                          code: '', 
                          type: 'sub', 
                          parentId: mainCat?.main_category_id || mainCat?.id || ''
                        });
                        setQuickAddCategoryOpen(true);
                      }}
                      createNewText="+ Add Sub-Category"
                    />
                  </div>
                </div>
                
                {/* Status */}
                <div>
                  <Label className="text-stone-900">Status</Label>
                  <Select 
                    value={inlineTaskData.status || 'not_started'}
                    onValueChange={(v) => setInlineTaskData({...inlineTaskData, status: v})}
                  >
                    <SelectTrigger className="bg-white text-stone-900 border-stone-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Task Schedule Section */}
                <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/50">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <CalendarCheck size={16} />
                    Task Schedule (Calendar Integration)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-700 text-sm">Start Date</Label>
                      <Input 
                        type="date"
                        value={inlineTaskData.start_date?.split('T')[0] || ''}
                        onChange={(e) => setInlineTaskData({...inlineTaskData, start_date: e.target.value})}
                        className="bg-white text-stone-900 border-stone-300"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-700 text-sm">End Date (Deadline) <span className="text-red-500">*</span></Label>
                      <Input 
                        type="date"
                        value={inlineTaskData.end_date?.split('T')[0] || inlineTaskData.deadline?.split('T')[0] || ''}
                        onChange={(e) => setInlineTaskData({...inlineTaskData, end_date: e.target.value, deadline: e.target.value})}
                        className="bg-white text-stone-900 border-stone-300"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    These dates are displayed on the Project Calendar for execution monitoring.
                  </p>
                </div>
                
                {/* Estimated Values Section */}
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Clipboard size={16} />
                    Estimated (From Design)
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-stone-700 text-sm">Quantity</Label>
                      <Input 
                        type="number"
                        min="0"
                        step="0.01"
                        value={inlineTaskData.quantity || 1}
                        onChange={(e) => setInlineTaskData({...inlineTaskData, quantity: parseFloat(e.target.value) || 1})}
                        className="bg-white text-stone-900 border-stone-300"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-700 text-sm">Unit</Label>
                      <Select 
                        value={inlineTaskData.unit || 'No.s'}
                        onValueChange={(v) => setInlineTaskData({...inlineTaskData, unit: v})}
                      >
                        <SelectTrigger className="bg-white text-stone-900 border-stone-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-stone-700 text-sm">{isCtV(selectedWorksheet) ? 'Client Rate (₹)' : 'Vendor Rate (₹)'}</Label>
                      <Input 
                        type="number"
                        min="0"
                        step="0.01"
                        value={isCtV(selectedWorksheet)
                          ? (inlineTaskData._clientRate ?? inlineTaskData.client_rate ?? inlineTaskData.rate ?? 0)
                          : (inlineTaskData._vendorRate ?? inlineTaskData.vendor_rate ?? inlineTaskData.rate ?? 0)}
                        onChange={(e) => {
                          const rv = parseFloat(e.target.value) || 0;
                          const pct1 = inlineTaskData.admin_markup_percentage ?? selectedWorksheet?.admin_markup_percentage ?? 20;
                          if (isCtV(selectedWorksheet)) {
                            const ar = Math.round(rv * (1 - pct1 / 100) * 100) / 100;
                            setInlineTaskData({...inlineTaskData, rate: rv, client_rate: rv, admin_rate: ar, _clientRate: rv});
                          } else {
                            const ar = Math.round(rv * (1 + pct1 / 100) * 100) / 100;
                            setInlineTaskData({...inlineTaskData, rate: rv, vendor_rate: rv, admin_rate: ar, _vendorRate: rv});
                          }
                        }}
                        className="bg-white text-stone-900 border-stone-300"
                      />
                    </div>
                  </div>
                  {(() => {
                    const ctv = isCtV(selectedWorksheet);
                    const baseRate = ctv
                      ? (inlineTaskData._clientRate ?? inlineTaskData.client_rate ?? inlineTaskData.rate ?? 0)
                      : (inlineTaskData._vendorRate ?? inlineTaskData.vendor_rate ?? inlineTaskData.rate ?? 0);
                    const pct1 = inlineTaskData.admin_markup_percentage ?? selectedWorksheet?.admin_markup_percentage ?? 20;
                    const ar = ctv
                      ? Math.round(baseRate * (1 - pct1 / 100) * 100) / 100
                      : Math.round(baseRate * (1 + pct1 / 100) * 100) / 100;
                    if (inlineTaskData.quantity > 0 && baseRate > 0) {
                      return (
                        <div className="mt-3 text-sm flex gap-4">
                          <span className="text-stone-600">{ctv ? 'Client' : 'Vendor'} Amt: <span className="font-bold text-purple-700">₹{((inlineTaskData.quantity || 0) * baseRate).toLocaleString()}</span></span>
                          <span className="text-stone-400">Admin Amt: ₹{((inlineTaskData.quantity || 0) * ar).toLocaleString()}</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Markup/Markdown Override — Model-aware */}
                <div className={`border rounded-lg p-4 ${isCtV(selectedWorksheet) ? 'border-blue-200 bg-blue-50/30' : 'border-purple-200 bg-purple-50/30'}`}>
                  <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isCtV(selectedWorksheet) ? 'text-blue-800' : 'text-purple-800'}`}>
                    <TrendingUp size={16} />
                    {isCtV(selectedWorksheet) ? 'Markdown Override (Client → Admin → Vendor)' : 'Markup Override (Vendor → Admin → Client)'}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-700 text-sm">{isCtV(selectedWorksheet) ? 'Admin Markdown %' : 'Admin Markup %'}</Label>
                      <Input 
                        type="number"
                        min="0"
                        max="200"
                        step="0.1"
                        value={inlineTaskData.admin_markup_percentage ?? selectedWorksheet?.admin_markup_percentage ?? 20}
                        onChange={(e) => setInlineTaskData({...inlineTaskData, admin_markup_percentage: parseFloat(e.target.value) || 0})}
                        className="bg-white text-stone-900 border-stone-300"
                        data-testid="task-admin-markup"
                      />
                      <p className="text-xs text-stone-400 mt-0.5">
                        {isCtV(selectedWorksheet)
                          ? `Admin Rate = Client Rate × (1 − ${inlineTaskData.admin_markup_percentage ?? 20}%)`
                          : `Admin Rate = Vendor Rate × (1 + ${inlineTaskData.admin_markup_percentage ?? 20}%)`}
                      </p>
                    </div>
                    <div>
                      <Label className="text-stone-700 text-sm">{isCtV(selectedWorksheet) ? 'Vendor Markdown %' : 'Client Markup %'}</Label>
                      <Input 
                        type="number"
                        min="0"
                        max="200"
                        step="0.1"
                        value={inlineTaskData.client_markup_percentage ?? selectedWorksheet?.client_markup_percentage ?? 20}
                        onChange={(e) => setInlineTaskData({...inlineTaskData, client_markup_percentage: parseFloat(e.target.value) || 0})}
                        className="bg-white text-stone-900 border-stone-300"
                        data-testid="task-client-markup"
                      />
                      <p className="text-xs text-stone-400 mt-0.5">
                        {isCtV(selectedWorksheet)
                          ? `Vendor Rate = Admin Rate × (1 − ${inlineTaskData.client_markup_percentage ?? 20}%)`
                          : `Client Rate = Admin Rate × (1 + ${inlineTaskData.client_markup_percentage ?? 20}%)`}
                      </p>
                    </div>
                  </div>
                  {(() => {
                    const ctv = isCtV(selectedWorksheet);
                    const baseRate = ctv
                      ? (inlineTaskData._clientRate ?? inlineTaskData.client_rate ?? inlineTaskData.rate ?? 0)
                      : (inlineTaskData._vendorRate ?? inlineTaskData.vendor_rate ?? inlineTaskData.rate ?? 0);
                    if (baseRate <= 0) return null;
                    const pct1 = inlineTaskData.admin_markup_percentage ?? selectedWorksheet?.admin_markup_percentage ?? 20;
                    const pct2 = inlineTaskData.client_markup_percentage ?? selectedWorksheet?.client_markup_percentage ?? 20;
                    let vr, ar, cr;
                    if (ctv) {
                      cr = baseRate;
                      ar = Math.round(cr * (1 - pct1 / 100) * 100) / 100;
                      vr = Math.round(ar * (1 - pct2 / 100) * 100) / 100;
                    } else {
                      vr = baseRate;
                      ar = Math.round(vr * (1 + pct1 / 100) * 100) / 100;
                      cr = Math.round(ar * (1 + pct2 / 100) * 100) / 100;
                    }
                    return (
                    <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                      <div className={`bg-white rounded p-2 border ${ctv ? 'border-blue-200' : 'border-purple-200'}`}>
                        <span className={`block text-xs ${ctv ? 'text-blue-600' : 'text-purple-600'}`}>{ctv ? `Client Rate (Input)` : `Vendor Rate (Input)`}</span>
                        <span className={`font-semibold ${ctv ? 'text-blue-700' : 'text-purple-700'}`}>₹{(ctv ? cr : vr).toLocaleString()}</span>
                      </div>
                      <div className="bg-white rounded p-2 border border-stone-200">
                        <span className="text-stone-500 block text-xs">Admin Rate ({ctv ? `−${pct1}%` : `+${pct1}%`})</span>
                        <span className="font-semibold">₹{ar.toLocaleString()}</span>
                      </div>
                      <div className={`bg-white rounded p-2 border ${ctv ? 'border-purple-200' : 'border-green-200'}`}>
                        <span className={`block text-xs ${ctv ? 'text-purple-600' : 'text-green-600'}`}>{ctv ? `Vendor Rate (−${pct2}%)` : `Client Rate (+${pct2}%)`}</span>
                        <span className={`font-semibold ${ctv ? 'text-purple-700' : 'text-green-700'}`}>₹{(ctv ? vr : cr).toLocaleString()}</span>
                      </div>
                    </div>
                    );
                  })()}
                </div>
                
                {/* Client Approval Section */}
                <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/50">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    Client Approval
                  </h4>
                  <div className="flex items-center gap-2">
                    <div className={`text-sm px-2 py-1 rounded ${
                      inlineTaskData.client_approved 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-stone-100 text-stone-500'
                    }`}>
                      {inlineTaskData.client_approved ? '✓ Approved' : 'Pending Approval'}
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
                  <Button variant="outline" onClick={() => setInlineTaskEditOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveInlineTaskEdit} className="apple-button">
                    <Save size={14} className="mr-2" />
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-stone-500">Task not found</div>
            )}
          </DialogContent>
        </Dialog>

        {/* BOQ Confirmation Dialog */}
        <ConfirmBOQDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen} confirmWorksheet={confirmWorksheet} confirmStatus={confirmStatus} setConfirmStatus={setConfirmStatus} confirmNotes={confirmNotes} setConfirmNotes={setConfirmNotes} confirming={confirming} onConfirm={handleConfirmBOQ} />

        {/* PHASE 2: Clone BOQ Dialog */}
        <Dialog open={cloneBOQDialogOpen} onOpenChange={setCloneBOQDialogOpen}>
          <DialogContent className="max-w-xl" data-testid="clone-boq-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
                <Copy className="text-blue-600" />
                Clone BOQ to Another Project
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCloneBOQ} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-2">This will copy:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All tasks with titles, quantities, units</li>
                  <li>Category mappings</li>
                  <li>Descriptions (optional)</li>
                </ul>
                <p className="mt-2 text-blue-600"><strong>Not copied:</strong> Rates, vendor assignments, payment records</p>
              </div>
              <div>
                <Label className="text-stone-900">Source Project (Copy FROM) *</Label>
                <SearchableCombobox
                  options={projects.filter(isSelectableProject).map(p => ({ value: p.id, label: p.name }))}
                  value={cloneBOQData.source_project_id}
                  onValueChange={(value) => setCloneBOQData({ ...cloneBOQData, source_project_id: value })}
                  placeholder="Select source project..."
                  searchPlaceholder="Type to search..."
                  allowCreate={false}
                />
              </div>
              <div>
                <Label className="text-stone-900">Target Project (Copy TO) *</Label>
                <SearchableCombobox
                  options={projects.filter(p => isSelectableProject(p) && p.id !== cloneBOQData.source_project_id).map(p => ({ value: p.id, label: p.name }))}
                  value={cloneBOQData.target_project_id}
                  onValueChange={(value) => setCloneBOQData({ ...cloneBOQData, target_project_id: value })}
                  placeholder="Select target project..."
                  searchPlaceholder="Type to search..."
                  allowCreate={false}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="include_descriptions"
                  checked={cloneBOQData.include_descriptions}
                  onCheckedChange={(checked) => setCloneBOQData({ ...cloneBOQData, include_descriptions: checked })}
                />
                <Label htmlFor="include_descriptions" className="text-stone-700">Include task descriptions</Label>
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setCloneBOQDialogOpen(false)} className="text-stone-900">Cancel</Button>
                <Button type="submit" disabled={cloneBOQLoading} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {cloneBOQLoading ? 'Cloning...' : <><Copy size={16} className="mr-2" />Clone BOQ</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* PHASE 2: Create Vendor Quote Request Dialog */}
        <Dialog open={vendorQuoteDialogOpen} onOpenChange={setVendorQuoteDialogOpen}>
          <DialogContent className="max-w-xl" data-testid="vendor-quote-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
                <Link className="text-green-600" />
                Create Vendor Quote Link
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateVendorQuoteRequest} className="space-y-4">
              <div className="bg-green-50 p-3 rounded-lg text-sm text-green-800">
                <p>Generate a unique public link for a vendor to submit their pricing. The vendor will see the task scope and can submit their rates.</p>
              </div>
              <div>
                <Label className="text-stone-900">Project *</Label>
                <SearchableCombobox
                  options={projects.filter(isSelectableProject).map(p => ({ value: p.id, label: p.name }))}
                  value={vendorQuoteData.project_id}
                  onValueChange={(value) => setVendorQuoteData({ ...vendorQuoteData, project_id: value })}
                  placeholder="Select project..."
                  searchPlaceholder="Type to search..."
                  allowCreate={false}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-900">Vendor Name *</Label>
                  <Input value={vendorQuoteData.vendor_name} onChange={(e) => setVendorQuoteData({ ...vendorQuoteData, vendor_name: e.target.value })} placeholder="e.g., ABC Contractors" className="bg-white text-stone-900" />
                </div>
                <div>
                  <Label className="text-stone-900">Vendor Email <span className="text-stone-400 text-xs">(Optional)</span></Label>
                  <Input type="email" value={vendorQuoteData.vendor_email} onChange={(e) => setVendorQuoteData({ ...vendorQuoteData, vendor_email: e.target.value })} placeholder="vendor@example.com" className="bg-white text-stone-900" />
                </div>
              </div>
              <div>
                <Label className="text-stone-900">Instructions for Vendor</Label>
                <Textarea value={vendorQuoteData.instructions} onChange={(e) => setVendorQuoteData({ ...vendorQuoteData, instructions: e.target.value })} placeholder="Please provide your best rates..." className="bg-white text-stone-900" rows={3} />
              </div>
              <div>
                <Label className="text-stone-900">Link Expiry (Days)</Label>
                <Select value={String(vendorQuoteData.expiry_days)} onValueChange={(value) => setVendorQuoteData({ ...vendorQuoteData, expiry_days: parseInt(value) })}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="20">20 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setVendorQuoteDialogOpen(false)} className="text-stone-900">Cancel</Button>
                <Button type="submit" disabled={vendorQuoteLoading} className="bg-green-600 hover:bg-green-700 text-white">
                  {vendorQuoteLoading ? 'Creating...' : <><Link size={16} className="mr-2" />Create Link & Copy</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* PHASE 2: View Vendor Quote Requests Dialog */}
        <Dialog open={viewQuoteRequestsDialogOpen} onOpenChange={setViewQuoteRequestsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="view-quote-requests-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
                <ClipboardCheck className="text-amber-600" />
                Vendor Quote Requests
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {vendorQuoteRequests.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <ClipboardCheck size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No vendor quote requests yet</p>
                  <Button variant="outline" className="mt-3" onClick={() => { setViewQuoteRequestsDialogOpen(false); setVendorQuoteDialogOpen(true); }}>Create First Request</Button>
                </div>
              ) : (
                vendorQuoteRequests.map((req) => (
                  <div key={req.id} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-stone-900">{req.vendor_name}</span>
                        {req.vendor_email && <span className="text-stone-500 ml-2">({req.vendor_email})</span>}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        req.status === 'submitted' ? 'bg-green-100 text-green-700' :
                        req.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-700'
                      }`}>{req.status?.toUpperCase() || 'PENDING'}</span>
                    </div>
                    <div className="text-sm text-stone-600">
                      <p><strong>Project:</strong> {req.project_name}</p>
                      <p><strong>Expires:</strong> {formatDate(req.expiry_date)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={async () => {
                        const url = `${window.location.origin}/vendor-quote/${req.token}`;
                        try { await navigator.clipboard.writeText(url); toast.success('Link copied!'); }
                        catch { toast.info(`Copy: ${url}`, { duration: 8000 }); }
                      }} className="text-blue-600 hover:bg-blue-50"><Copy size={14} className="mr-1" />Copy Link</Button>
                      <Button variant="outline" size="sm" onClick={() => window.open(`/vendor-quote/${req.token}`, '_blank')} className="text-green-600 hover:bg-green-50"><ExternalLink size={14} className="mr-1" />Open</Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteVendorQuoteRequest(req.id)} className="text-red-600 hover:bg-red-50"><Trash2 size={14} className="mr-1" />Delete</Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* PHASE 6: Send BOQ via Email Dialog */}
        <EmailComposeDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          projectId={emailProjectId}
          templateType="boq_sheet"
        />

        {/* Pricing Model Comparison Dialog */}
        <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="compare-models-dialog">
            {(() => {
              const projectWs = worksheets.filter(w => w.project_id === compareProjectId);
              if (!projectWs.length) return <div className="text-stone-500 text-center py-8">No BOQ worksheets found for this project.</div>;
              const ws = projectWs[0];
              const currentModel = ws.pricing_model || 'vendor_to_client';
              const altModel = currentModel === 'vendor_to_client' ? 'client_to_vendor' : 'vendor_to_client';
              const pct1 = ws.admin_markup_percentage || 20;
              const pct2 = ws.client_markup_percentage || 20;
              const comparisonItems = getComparisonData(compareProjectId) || [];
              const totalItems = projectWs.reduce((sum, w) => sum + (w.items || []).filter(i => !i.is_discount).length, 0);

              // Calculate totals across ALL items (not just top 10 shown), including GST, discount, overheads
              let curClientTotal = 0, curVendorTotal = 0, altClientTotal = 0, altVendorTotal = 0;
              const allProjectItems = projectWs.flatMap(w => (w.items || []).filter(i => !i.is_discount && i.item_type !== 'header'));
              allProjectItems.forEach(item => {
                const vr = parseFloat(item.vendor_rate || 0);
                const cr = parseFloat(item.client_rate || 0);
                const qty = parseFloat(item.quantity || 0);
                const gstPct = parseFloat(item.gst_percentage ?? 0);
                const curClientBase = qty * cr;
                const curVendorBase = qty * vr;
                curClientTotal += curClientBase + curClientBase * (gstPct / 100);
                curVendorTotal += curVendorBase + curVendorBase * (gstPct / 100);
                // Simulate alt model rates
                let altCr, altVr;
                if (currentModel === 'vendor_to_client') {
                  altCr = cr;
                  altVr = Math.round(cr * (1 - pct1 / 100) * (1 - pct2 / 100) * 100) / 100;
                } else {
                  altVr = vr;
                  altCr = Math.round(vr * (1 + pct1 / 100) * (1 + pct2 / 100) * 100) / 100;
                }
                const altClientBase = qty * altCr;
                const altVendorBase = qty * altVr;
                altClientTotal += altClientBase + altClientBase * (gstPct / 100);
                altVendorTotal += altVendorBase + altVendorBase * (gstPct / 100);
              });
              // Apply discount and overheads to totals
              const applyDiscountAndOverheads = (rawTotal) => {
                let d = 0;
                if (ws.discount_type === 'percentage' && parseFloat(ws.discount_percentage || 0) > 0) d = Math.round(rawTotal * parseFloat(ws.discount_percentage) / 100);
                else if (parseFloat(ws.discount_value || 0) > 0) d = Math.round(parseFloat(ws.discount_value));
                const afterD = rawTotal - d;
                const oh = Math.round(afterD * (parseFloat(ws.freight_pct || 0) + parseFloat(ws.labour_handling_pct || 0) + parseFloat(ws.consultancy_pct || 0)) / 100);
                return Math.round(afterD + oh);
              };
              // We show raw subtotal+GST in comparison, not overheads
              curClientTotal = Math.round(curClientTotal);
              curVendorTotal = Math.round(curVendorTotal);
              altClientTotal = Math.round(altClientTotal);
              altVendorTotal = Math.round(altVendorTotal);

              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <TrendingUp size={20} className="text-amber-600" />
                      Pricing Model Comparison — {getProjectName(compareProjectId)}
                    </DialogTitle>
                  </DialogHeader>

                  {/* Current vs Alternative Summary */}
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className={`rounded-xl p-4 border-2 ${currentModel === 'vendor_to_client' ? 'border-purple-300 bg-purple-50/50' : 'border-blue-300 bg-blue-50/50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${currentModel === 'vendor_to_client' ? 'bg-purple-200 text-purple-800' : 'bg-blue-200 text-blue-800'}`}>CURRENT</span>
                        <span className="text-sm font-semibold text-stone-800">{currentModel === 'vendor_to_client' ? 'Vendor → Client' : 'Client → Vendor'}</span>
                      </div>
                      <p className="text-xs text-stone-500 mb-3">{currentModel === 'vendor_to_client' ? `Vendor Rate + ${pct1}% Admin Markup + ${pct2}% Client Markup` : `Client Rate − ${pct1}% Admin Markdown − ${pct2}% Vendor Markdown`}</p>
                      <div className="flex justify-between text-sm">
                        <div><span className="text-stone-500">Client Total:</span> <span className="font-bold text-stone-900">₹{curClientTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
                        <div><span className="text-stone-500">Vendor Total:</span> <span className="font-bold text-stone-900">₹{curVendorTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
                      </div>
                    </div>
                    <div className={`rounded-xl p-4 border-2 border-dashed ${altModel === 'vendor_to_client' ? 'border-purple-300 bg-purple-50/30' : 'border-blue-300 bg-blue-50/30'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">PREVIEW</span>
                        <span className="text-sm font-semibold text-stone-800">{altModel === 'vendor_to_client' ? 'Vendor → Client' : 'Client → Vendor'}</span>
                      </div>
                      <p className="text-xs text-stone-500 mb-3">{altModel === 'vendor_to_client' ? `Vendor Rate + ${pct1}% Admin Markup + ${pct2}% Client Markup` : `Client Rate − ${pct1}% Admin Markdown − ${pct2}% Vendor Markdown`}</p>
                      <div className="flex justify-between text-sm">
                        <div><span className="text-stone-500">Client Total:</span> <span className="font-bold text-amber-700">₹{altClientTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
                        <div><span className="text-stone-500">Vendor Total:</span> <span className="font-bold text-amber-700">₹{altVendorTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
                      </div>
                    </div>
                  </div>

                  {/* Detailed item comparison */}
                  <div className="mt-4 border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-stone-100">
                          <th className="text-left p-2 font-semibold text-stone-700" colSpan={1}>Item</th>
                          <th className="text-center p-2 font-semibold text-stone-700 border-l" colSpan={3}>
                            Current ({currentModel === 'vendor_to_client' ? 'V→C' : 'C→V'})
                          </th>
                          <th className="text-center p-2 font-semibold text-amber-700 border-l bg-amber-50/50" colSpan={3}>
                            Preview ({altModel === 'vendor_to_client' ? 'V→C' : 'C→V'})
                          </th>
                        </tr>
                        <tr className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500">
                          <th className="text-left p-1.5 pl-2">Name / Qty</th>
                          <th className="text-right p-1.5 border-l">Vendor</th>
                          <th className="text-right p-1.5">Admin</th>
                          <th className="text-right p-1.5">Client</th>
                          <th className="text-right p-1.5 border-l bg-amber-50/30">Vendor</th>
                          <th className="text-right p-1.5 bg-amber-50/30">Admin</th>
                          <th className="text-right p-1.5 bg-amber-50/30">Client</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonItems.map((item, idx) => (
                          <tr key={idx} className="border-t hover:bg-stone-50/50">
                            <td className="p-1.5 pl-2">
                              <div className="font-medium text-stone-800 truncate max-w-[180px]">{item.title}</div>
                              <div className="text-stone-400">Qty: {item.qty}</div>
                            </td>
                            <td className="text-right p-1.5 border-l font-mono">₹{item.current.vr.toLocaleString()}</td>
                            <td className="text-right p-1.5 font-mono">₹{item.current.ar.toLocaleString()}</td>
                            <td className="text-right p-1.5 font-mono">₹{item.current.cr.toLocaleString()}</td>
                            <td className={`text-right p-1.5 border-l font-mono bg-amber-50/20 ${item.alt.vr !== item.current.vr ? 'text-amber-700 font-bold' : ''}`}>₹{item.alt.vr.toLocaleString()}</td>
                            <td className={`text-right p-1.5 font-mono bg-amber-50/20 ${item.alt.ar !== item.current.ar ? 'text-amber-700 font-bold' : ''}`}>₹{item.alt.ar.toLocaleString()}</td>
                            <td className={`text-right p-1.5 font-mono bg-amber-50/20 ${item.alt.cr !== item.current.cr ? 'text-amber-700 font-bold' : ''}`}>₹{item.alt.cr.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {totalItems > 10 && (
                      <div className="text-center py-2 text-xs text-stone-400 bg-stone-50 border-t">
                        Showing top 10 of {totalItems} items
                      </div>
                    )}
                  </div>

                  {/* Switch action */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-xs text-stone-500 max-w-sm">
                      Switching will recalculate all BOQ item rates for this project. This action can be reversed.
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setCompareDialogOpen(false)} data-testid="compare-cancel-btn">Cancel</Button>
                      <Button 
                        onClick={() => handleSwitchPricingModel(compareProjectId)}
                        disabled={compareSwitching}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="compare-switch-btn"
                      >
                        {compareSwitching ? 'Switching...' : `Switch to ${altModel === 'vendor_to_client' ? 'V→C' : 'C→V'}`}
                      </Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* iter-338z8 — New empty BOQ dialog */}
        <Dialog open={newBoqDialogOpen} onOpenChange={(v) => { if (!creatingNewBoq) setNewBoqDialogOpen(v); }}>
          <DialogContent className="max-w-md" data-testid="new-boq-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900 flex items-center gap-2">
                <Plus size={18} className="text-stone-900" />
                Create a new BOQ
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-stone-50 border border-stone-200 rounded-md p-2.5 text-xs text-stone-600">
                Pick a project and (optionally) name this BOQ — you&apos;ll be taken to Edit BOQ where you can add line items via AI Task Creator, presets, or manually. No tasks required.
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Project</Label>
                <SearchableCombobox
                  options={[...projects]
                    .filter(isSelectableProject)
                    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                    .map((p) => ({ value: p.id, label: p.name }))}
                  value={newBoqProjectId}
                  onValueChange={setNewBoqProjectId}
                  placeholder="Select a project…"
                  createNewText="+ Create new project"
                  onCreateNew={() => setCreateProjectDialogOpen(true)}
                  data-testid="new-boq-project-picker"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">BOQ Name (optional)</Label>
                <Input
                  value={newBoqDisplayName}
                  onChange={(e) => setNewBoqDisplayName(e.target.value)}
                  placeholder="e.g. Living Room Woodwork"
                  className="bg-white text-stone-900 mt-0.5"
                  data-testid="new-boq-name-input"
                  onKeyDown={(e) => e.key === 'Enter' && newBoqProjectId && handleCreateEmptyBOQ()}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setNewBoqDialogOpen(false)} disabled={creatingNewBoq}>Cancel</Button>
              <Button
                onClick={handleCreateEmptyBOQ}
                disabled={creatingNewBoq || !newBoqProjectId}
                className="bg-stone-900 hover:bg-stone-800 text-white"
                data-testid="new-boq-create-btn"
              >
                <Plus size={14} className="mr-1" />
                {creatingNewBoq ? 'Creating…' : 'Create & Edit'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* iter-381 — Quick Create Project dialog reachable from the
            "+ Create new project" combobox action above. On success the
            new project id is auto-selected in the BOQ dialog. */}
        <CreateProjectDialog
          open={createProjectDialogOpen}
          onOpenChange={setCreateProjectDialogOpen}
          onProjectCreated={(proj) => {
            if (proj?.id) {
              // Prepend so the new project appears at the top of the picker.
              setProjects((prev) => [proj, ...prev]);
              setNewBoqProjectId(proj.id);
            }
          }}
        />
      </div>
    </Layout>
  );
}
